<template>
  <div>
    <Header />

    <!-- 麵包屑導覽 -->
    <nav aria-label="breadcrumb" class="breadcrumb_box">
      <div class="container">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/Profile" title="首頁">Home</router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">
            International Student Application System
          </li>
        </ol>
      </div>
    </nav>

    <!-- 主要內容區 -->
    <div class="main form">
      <!-- 無障礙快捷鍵 -->
      <a accesskey="c" class="accesskey" id="accesskey_c" href="javascript:void(0);" title="中央內容區塊"
        onclick="document.getElementById('accesskey_c').scrollIntoView();">:::
      </a>
      <div class="container">
        <div class="box" :class="{ 'submitted-readonly': isSubmitted }">
          <!-- 頁面標題與說明按鈕 -->
          <div class="title">
            <h2>International Student Application System</h2>
            <div class="btn_box">
              <button class="tours_btn" @click="startTour">
                <svg width="28" height="28" viewBox="0 0 28 28">
                  <defs>
                    <clipPath id="clip-path">
                      <rect id="_3" data-name="3" width="28" height="28" transform="translate(5575 10526)" fill="none"
                        stroke="#707070" stroke-width="1" />
                    </clipPath>
                  </defs>
                  <g id="icon_instructions" transform="translate(-5575 -10526)" clip-path="url(#clip-path)">
                    <g id="_2" data-name="2" transform="translate(5575.14 10526.5)">
                      <path id="_1" data-name="1"
                        d="M23.14,21.36a1,1,0,0,0,1-1V3.42A1.929,1.929,0,0,0,22.21,1.5H8.69V19.358h-2V1.5H5.93A3.077,3.077,0,0,0,2.86,4.57V22.43c0,.011.006.021.007.032A3.069,3.069,0,0,0,5.934,25.5h17.2a1,1,0,0,0,0-2H5.934a1.07,1.07,0,0,1,0-2.14H23.14Zm-7.751-15a1.007,1.007,0,0,1,1.021,1,1,1,0,0,1-2,0,1,1,0,0,1,.979-1ZM14.41,11.19a1,1,0,0,1,2,0v3.96a1,1,0,0,1-2,0Z"
                        fill="#580d63" />
                    </g>
                  </g>
                </svg>
                Instructions
              </button>
            </div>
          </div>

          <!-- 進度條 -->
          <ul class="progress_bar">
            <li v-for="(item, index) in progressItems" :key="index">
              <router-link :to="item.to" :class="['step_box', getStepClass(index)]">
                <div class="step">
                  <p v-if="index != 6">Step {{ index + 1 }}</p>
                  <p v-else>Payment</p>
                  <p>{{ item.title }}</p>
                </div>
                <div class="line"></div>
              </router-link>
            </li>
          </ul>

          <!-- 已提交提示訊息 -->
          <div v-if="isSubmitted" class="bg"
            style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 15px 20px; margin-bottom: 20px;">
            <p style="margin: 0; color: #856404; font-weight: bold; font-size: 16px;">
              ⚠️ Application submitted and under review. View-only mode.
            </p>
          </div>

          <!-- 載入指示器 -->
          <div v-if="isLoading" class="loading-container" style="text-align: center; padding: 40px;">
            <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
              <span class="sr-only">Loading...</span>
            </div>
            <p style="margin-top: 20px; font-size: 16px;">Loading form data...</p>
          </div>

          <!-- 表單內容說明 -->
          <div v-else class="bg">
            <div class="title_box description">
              <h3>{{ stepContent.title }}</h3>
            </div>
            <div v-if="stepContent.content" class="text_box" v-html="stepContent.content"></div>
          </div>

          <!-- 個人基本資料區塊 -->
          <div class="bg">
            <div class="title_box personal_box_order">
              <h3>Personal Data</h3>
            </div>

            <div class="personal_box">
              <!-- 英文姓名 -->
              <div class="form-group full" id="step2"
                :class="['form-group full', { active: isEnglishNameFilled, focused: isEnglishNameFocused, notFilledIn: showValidationErrors && (!isNameFirstValid || !isNameLastValid) }]">
                <div class="englishName_box">
                  <input type="text" class="form-control" id="nameFirst" v-model="formData.nameFirst"
                    @focus="isEnglishNameFocused = true" @blur="isEnglishNameFocused = false" required
                    placeholder="name first" :disabled="isSubmitted" />
                  <input type="text" class="form-control" id="nameMiddle" v-model="formData.nameMiddle"
                    @focus="isEnglishNameFocused = true" @blur="isEnglishNameFocused = false" required
                    placeholder="name middle" :disabled="isSubmitted" />
                  <input type="text" class="form-control" id="nameLast" v-model="formData.nameLast"
                    @focus="isEnglishNameFocused = true" @blur="isEnglishNameFocused = false" required
                    placeholder="name last" :disabled="isSubmitted" />
                  <label for="nameFirst"><span class="required">*</span>English Name</label>
                </div>
                <p class="notice">Please print as it appears on your passport name(s) and do not use abbreviations. Fill
                  out your name (the beginning letter of each word) in capital letters. Ex. Andrew Shu</p>
                <div v-if="showValidationErrors && (!isNameFirstValid || !isNameLastValid)" class="notFilledIn_text">Not
                  filled in</div>
              </div>

              <!-- 中文姓名 -->
              <div :class="['form-group full', { active: formData.chineseName }]" id="step3">
                <input type="text" class="form-control" id="chineseName" required v-model="formData.chineseName"
                  :disabled="isSubmitted" />
                <label for="chineseName">Chinese Name</label>
                <p class="notice">(Fill out in Chinese only if applicable)</p>
              </div>

              <div
                :class="['form-group', { active: formData.birth, notFilledIn: showValidationErrors && !isBirthValid }]">
                <input type="date" class="form-control" id="birth" placeholder="" v-model="formData.birth"
                  :disabled="isSubmitted" />
                <label for="birth"><span class="required">*</span>Date of Birth</label>
                <div v-if="showValidationErrors && !isBirthValid" class="notFilledIn_text">Not filled in</div>
              </div>

              <div
                :class="['form-group', { active: formData.contactEmail, notFilledIn: showValidationErrors && !isContactEmailValid }]">
                <input type="text" class="form-control" id="contactEmail" required v-model="formData.contactEmail"
                  :disabled="isSubmitted" />
                <label for="contactEmail"><span class="required">*</span>Contact Email</label>
                <div v-if="showValidationErrors && !isContactEmailValid" class="notFilledIn_text">Not filled in</div>
              </div>

              <div :class="['form-group checkForm_group', { notFilledIn: showValidationErrors && !isGenderValid }]">
                <label for=""><span class="required">*</span>Gender</label>

                <div class="check_group">
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="Gender" id="genderMale" v-model="formData.gender"
                      value="1" :disabled="isSubmitted" />
                    <label class="form-check-label" for="genderMale">
                      Male
                    </label>
                  </div>
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="Gender" id="genderFemale"
                      v-model="formData.gender" value="2" :disabled="isSubmitted" />
                    <label class="form-check-label" for="genderFemale">
                      Female
                    </label>
                  </div>
                </div>
                <div v-if="showValidationErrors && !isGenderValid" class="notFilledIn_text">Not filled in</div>
              </div>

              <div class="form-group checkForm_group">
                <label for="">Marital Status</label>

                <div class="check_group">
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="scholarships" id="scholarships_y"
                      v-model="formData.maritalStatus" value="1" :disabled="isSubmitted" />
                    <label class="form-check-label" for="scholarships_y">
                      Single
                    </label>
                  </div>
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="scholarships" id="scholarships_n"
                      v-model="formData.maritalStatus" value="2" :disabled="isSubmitted" />
                    <label class="form-check-label" for="scholarships_n">
                      Married
                    </label>
                  </div>
                </div>
              </div>

              <div
                :class="['form-group', { active: formData.nativeLanguage, notFilledIn: showValidationErrors && !isNativeLanguageValid }]">
                <input type="text" class="form-control" id="nativeLanguage" required v-model="formData.nativeLanguage"
                  :disabled="isSubmitted" />
                <label for="nativeLanguage"><span class="required">*</span>Native Language</label>
                <div v-if="showValidationErrors && !isNativeLanguageValid" class="notFilledIn_text">Not filled in</div>
              </div>
            </div>

            <div class="personal_box2">
              <div :class="[
                'form-group file_box',
                { active: formData.photo || previewUrl },
              ]">
                <div class="file">
                  <input type="file" class="form-control" id="photo" accept="image/*" @change="onFileChange"
                    ref="fileInput" :disabled="isSubmitted" />
                  <label for="photo" style="z-index: 10;"><span class="required">*</span>Profile Picture</label>
                  <div class="text">
                    <div class="upload">upload</div>
                    <p class="notice">
                      Kindly upload the file in JPG, or PNG format, with a
                      maximum size of 10MB.
                    </p>
                  </div>
                </div>

                <!-- 預覽圖片 -->
                <div class="preview_box">
                  <div class="preview" v-if="previewUrl">
                    <img v-if="previewUrl.startsWith('/api')"
                      :src="$store.state.src + $store.state.subDirectory + previewUrl" alt="photo" />
                    <img v-else :src="previewUrl" alt="photo" />
                    <button type="button" class="clear_btn" @click="clearImage">
                      delete
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <hr class="line_hr" />
            <div class="title_box">
              <h3>Permanent Address (It shall be the address in your hometown)</h3>
            </div>

            <div
              :class="['form-group', { active: formData.street, notFilledIn: showValidationErrors && !isStreetValid }]">
              <input type="text" class="form-control" id="street" required v-model="formData.street"
                :disabled="isSubmitted" />
              <label for="street"><span class="required">*</span>Street</label>
              <div v-if="showValidationErrors && !isStreetValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div :class="['form-group', { active: formData.city, notFilledIn: showValidationErrors && !isCityValid }]">
              <input type="text" class="form-control" id="city" v-model="formData.city" required
                :disabled="isSubmitted" />
              <label for="city"><span class="required">*</span>City</label>
              <div v-if="showValidationErrors && !isCityValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group arrow_bg', { active: formData.country, notFilledIn: showValidationErrors && !isCountryValid }]">
              <select class="form-control" id="country" v-model="formData.country" :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="country"><span class="required">*</span>Country</label>
              <img src="img/icon_formArrow.svg" alt="" />
              <div v-if="showValidationErrors && !isCountryValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group', { active: formData.zipCode, notFilledIn: showValidationErrors && !isZipCodeValid }]">
              <input type="text" class="form-control" id="zipCode" v-model="formData.zipCode" required
                :disabled="isSubmitted" />
              <label for="zipCode"><span class="required">*</span>Zip code</label>
              <div v-if="showValidationErrors && !isZipCodeValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group', { active: isPhoneFilled, focused: isPhoneFocused, notFilledIn: showValidationErrors && !isPhoneValid }]">
              <div class="phone_box">
                <input type="text" class="form-control" id="phoneCountry" v-model="formData.phoneCountry"
                  @focus="isPhoneFocused = true" @blur="isPhoneFocused = false" required placeholder="country"
                  :disabled="isSubmitted" />
                <input type="text" class="form-control" id="phoneArea" v-model="formData.phoneArea"
                  @focus="isPhoneFocused = true" @blur="isPhoneFocused = false" required placeholder="code-area"
                  :disabled="isSubmitted" />
                <input type="text" class="form-control" id="phoneNumber" v-model="formData.phoneNumber"
                  @focus="isPhoneFocused = true" @blur="isPhoneFocused = false" required placeholder="code-number"
                  :disabled="isSubmitted" />
                <label for=""><span class="required">*</span>Phone</label>
              </div>
              <p class="notice">+Country code-Area code-number</p>
              <div v-if="showValidationErrors && !isPhoneValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div class="title_box">
              <h3>Mailing Address</h3>

              <div class="form-check">
                <input class="form-check-input" type="checkbox" value="" name="sameAsPermanent" id="sameAsPermanent1"
                  v-model="sameAsPermanent" :disabled="!canCopyAddress || isSubmitted" />
                <label class="form-check-label" for="sameAsPermanent1">
                  As same as Permanent Address
                </label>
              </div>
            </div>

            <div
              :class="['form-group', { active: formData.mailingStreet, notFilledIn: showValidationErrors && !isMailingStreetValid }]">
              <input type="text" class="form-control" id="street" required v-model="formData.mailingStreet"
                :readonly="sameAsPermanent" :disabled="isSubmitted" />
              <label for="mailingStreet"><span class="required">*</span>Street</label>
              <div v-if="showValidationErrors && !isMailingStreetValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group', { active: formData.mailingCity, notFilledIn: showValidationErrors && !isMailingCityValid }]">
              <input type="text" class="form-control" id="mailingCity" v-model="formData.mailingCity" required
                :disabled="isSubmitted" />
              <label for="mailingCity"><span class="required">*</span>City</label>
              <div v-if="showValidationErrors && !isMailingCityValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.mailingCountry, notFilledIn: showValidationErrors && !isMailingCountryValid },
            ]">
              <select class="form-control" id="mailingCountry" v-model="formData.mailingCountry"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="mailingCountry"><span class="required">*</span>Country</label>
              <img src="img/icon_formArrow.svg" alt="" />
              <div v-if="showValidationErrors && !isMailingCountryValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group', { active: formData.mailingZipCode, notFilledIn: showValidationErrors && !isMailingZipCodeValid }]">
              <input type="text" class="form-control" id="mailingZipCode" v-model="formData.mailingZipCode" required
                :disabled="isSubmitted" />
              <label for="mailingZipCode"><span class="required">*</span>Zip code</label>
              <div v-if="showValidationErrors && !isMailingZipCodeValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group', { active: isMailingPhoneFilled, focused: isMailingPhoneFocused, notFilledIn: showValidationErrors && !isMailingPhoneValid }]">
              <div class="phone_box">
                <input type="text" class="form-control" id="mailingPhoneCountry" v-model="formData.mailingPhoneCountry"
                  @focus="isMailingPhoneFocused = true" @blur="isMailingPhoneFocused = false" required
                  placeholder="country" :disabled="isSubmitted" />
                <input type="text" class="form-control" id="mailingPhoneArea" v-model="formData.mailingPhoneArea"
                  @focus="isMailingPhoneFocused = true" @blur="isMailingPhoneFocused = false" required
                  placeholder="code-area" :disabled="isSubmitted" />
                <input type="text" class="form-control" id="mailingPhoneNumber" v-model="formData.mailingPhoneNumber"
                  @focus="isMailingPhoneFocused = true" @blur="isMailingPhoneFocused = false" required
                  placeholder="code-number" :disabled="isSubmitted" />
                <label for=""><span class="required">*</span>Phone</label>
              </div>
              <p class="notice">+Country code-Area code-number</p>
              <div v-if="showValidationErrors && !isMailingPhoneValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Country of Citizenship And Passport Information</h3>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.countryOfBirth, notFilledIn: showValidationErrors && !isCountryOfBirthValid },
            ]">
              <select class="form-control" id="countryOfBirth" v-model="formData.countryOfBirth"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="countryOfBirth"><span class="required">*</span>Country of Birth</label>
              <img src="img/icon_formArrow.svg" alt="" />
              <div v-if="showValidationErrors && !isCountryOfBirthValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.countryOfCitizenship, notFilledIn: showValidationErrors && !isCountryOfCitizenshipValid },
            ]">
              <select class="form-control" id="countryOfCitizenship" v-model="formData.countryOfCitizenship"
                :disabled="isPaid || isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="countryOfCitizenship"><span class="required">*</span>Country of Citizenship</label>
              <img src="img/icon_formArrow.svg" alt="" />
              <div v-if="showValidationErrors && !isCountryOfCitizenshipValid" class="notFilledIn_text">Not filled in
              </div>
              <div v-if="isPaid" class="notice" style="color: #ff9800; font-weight: bold; margin-top: 5px;">
                ⚠️ The order has been paid, so the country of citizenship cannot be modified.
              </div>
            </div>

            <div :class="['form-group', { active: formData.passportNumber }]">
              <input type="text" class="form-control" id="passportNumber" v-model="formData.passportNumber" required
                :disabled="isSubmitted" />
              <label for="passportNumber">Passport Number</label>
              <p class="notice">If applicable</p>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Financial Resources (This is for NTHU's reference only. It is not regarded as the financial
                certificate.)</h3>
            </div>
            <div :class="['form-group full check_block', { notFilledIn: showValidationErrors && !isFinancialValid }]">
              <label for=""><span class="required">*</span>Financial Support</label>
              <p class="notice">Indicate the main source(s) of funding you plan to use to fund your fees and living
                expenses</p>

              <div class="check_group financialSupport_box">
                <div v-for="finance in availableFinances" :key="finance.fId" class="form-check">
                  <input class="form-check-input" type="checkbox" :value="finance.fId" :id="'finance_' + finance.fId"
                    v-model="selectedFinances[finance.fId].selected" :disabled="isSubmitted" />
                  <label class="form-check-label" :for="'finance_' + finance.fId">
                    {{ finance.name_EN || finance.name }}
                  </label>

                  <!-- 如果是獎學金選項，顯示獎學金輸入框 -->
                  <input v-if="finance.isScholarship && selectedFinances[finance.fId].selected" type="text"
                    class="form-control" :id="'finance_scholarship_' + finance.fId"
                    placeholder="Please indicate the named scholarship"
                    v-model="selectedFinances[finance.fId].scholarshipText" required />

                  <!-- 如果是其他選項，顯示其他文字輸入框 -->
                  <input v-if="finance.isOther && selectedFinances[finance.fId].selected" type="text"
                    class="form-control" :id="'finance_other_' + finance.fId" placeholder="Please specify"
                    v-model="selectedFinances[finance.fId].otherText" required />
                </div>
              </div>
              <div v-if="showValidationErrors && !isFinancialValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <!-- Available Scholarships Section -->
            <template v-if="formData.country && availableScholarships.length > 0"
              v-for="scholarship in availableScholarships">
              <hr class="line_hr" />
              <div class="title_box">
                <h3>{{ scholarship.name_EN }}</h3>
                <div v-if="scholarship.content" v-html="scholarship.content" style="margin-top: 20px;"></div>
                <a class="file_a" style="margin-left: 0px; margin-right: 10px;"
                  v-if="scholarship.needApplyDoc && scholarship.sampleFile"
                  :href="$store.state.src + $store.state.subDirectory + scholarship.sampleFile" target="_blank">📄
                  Download Sample File</a>
              </div>
              <div class="form-group check_block full"
                :class="{ notFilledIn: showValidationErrors && scholarshipSelections[scholarship.sId].isApply && scholarship.needApplyDoc && !scholarshipSelections[scholarship.sId].fileUrl }">
                <div class="check_group">
                  <div class="form-check">
                    <input class="form-check-input" type="radio" :name="'scholarship_' + scholarship.sId"
                      :id="'scholarship_' + scholarship.sId + '_yes'"
                      v-model="scholarshipSelections[scholarship.sId].isApply" :value="true" :disabled="isSubmitted" />
                    <label class="form-check-label" :for="'scholarship_' + scholarship.sId + '_yes'">
                      Apply
                    </label>
                  </div>
                  <div class="form-check">
                    <input class="form-check-input" type="radio" :name="'scholarship_' + scholarship.sId"
                      :id="'scholarship_' + scholarship.sId + '_no'"
                      v-model="scholarshipSelections[scholarship.sId].isApply" :value="false" :disabled="isSubmitted" />
                    <label class="form-check-label" :for="'scholarship_' + scholarship.sId + '_no'">
                      Not Apply
                    </label>
                  </div>
                </div>

                <!-- 檔案上傳區塊 - 當選擇申請且需要上傳文件時顯示 -->
                <div v-if="scholarshipSelections[scholarship.sId].isApply && scholarship.needApplyDoc"
                  class="scholarship_upload_box"
                  style="margin-top: 15px; padding: 15px; background: #f9f9f9; border-radius: 5px;">

                  <!-- 如果還沒有上傳檔案 -->
                  <div v-if="!scholarshipSelections[scholarship.sId].fileUrl" class="upload_section">
                    <input type="file" :id="'scholarship_file_' + scholarship.sId"
                      @change="onScholarshipFileChange($event, scholarship.sId)" style="display: none;"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" :disabled="isSubmitted" />
                    <label :for="'scholarship_file_' + scholarship.sId" class="btn upload_btn"
                      style="cursor: pointer; background: #007bff; color: white; padding: 8px 15px; border-radius: 4px; display: inline-block;">
                      Upload Application Document
                    </label>
                    <p class="notice" style="margin-top: 5px; margin-bottom: 0;">
                      Please upload your application document (PDF, DOC, DOCX, JPG, PNG, max 10MB)
                    </p>
                  </div>

                  <!-- 如果已經有檔案 -->
                  <div v-else class="uploaded_section">
                    <p style="margin-bottom: 10px; color: #dc3545; font-weight: bold;"
                      v-if="scholarshipSelections[scholarship.sId].fileUrl.startsWith('data')">
                      ⏳ Waiting for upload, File name:
                      {{ scholarshipSelections[scholarship.sId].fileName }}
                    </p>
                    <p style="margin-bottom: 10px; color: #28a745; font-weight: bold;" v-else>
                      ✓ Document uploaded successfully
                    </p>
                    <img v-if="scholarshipSelections[scholarship.sId].fileUrl.startsWith('data')"
                      :src="scholarshipSelections[scholarship.sId].fileUrl" style="width: 50%; height: auto;" />
                    <div style="display: flex; gap: 10px; align-items: center; margin-top: 10px;">
                      <button v-if="!scholarshipSelections[scholarship.sId].fileUrl.startsWith('data')" type="button"
                        class="btn" @click="downloadScholarshipFile(scholarship.sId)"
                        style="background: #28a745; color: white; padding: 8px 15px; border-radius: 4px; border: none; cursor: pointer;">
                        Download
                      </button>
                      <button type="button" class="btn" @click="removeScholarshipFile(scholarship.sId)"
                        style="background: #dc3545; color: white; padding: 8px 15px; border-radius: 4px; border: none; cursor: pointer;">
                        Remove
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  v-if="showValidationErrors && scholarshipSelections[scholarship.sId].isApply && scholarship.needApplyDoc && !scholarshipSelections[scholarship.sId].fileUrl"
                  class="notFilledIn_text">Application document is required</div>
              </div>
            </template>

            <template v-if="false">
              <hr class="line_hr" />

              <div class="title_box">
                <h3>ITRI & NTHU Coordinated Graduate Program(Scholarship & Internship)</h3>
                <p class="notice">Applicants must be the alumni of the Indian Institutes of Technology (IITs) or the one
                  who will graduate from IITs before January, 2024 For more information, refer to Scholarships at
                  http://oga.nthu.edu.tw/</p>
              </div>

              <div
                :class="['form-group full check_block', { notFilledIn: showValidationErrors && !isITRIProgramValid }]">
                <label for=""><span class="required">*</span>Applicants must be the alumni of
                  the Indian Institutes of Technology (IITs)</label>

                <div class="check_group">
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="ITRI" id="ITRIYes"
                      v-model="formData.isITRIProgram" value="1" :disabled="isSubmitted" />
                    <label class="form-check-label" for="ITRIYes">
                      Yes,apply for ITRI & NTHU Coordinated Graduate
                      Program(Scholarship &amp; Internship),
                    </label>
                  </div>
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="ITRI" id="ITRINot"
                      v-model="formData.isITRIProgram" value="2" :disabled="isSubmitted" />
                    <label class="form-check-label" for="ITRINot">
                      Not interested in ITRI &amp; NTHU Coordinated Graduate
                      Program(Scholarship &amp; Internship)
                    </label>
                  </div>

                  <div class="ITRI_yes_box bg" v-if="formData.isITRIProgram == 1">
                    <label class="label_title" for="">1. Education Background:</label>
                    <div :class="['form-group', { active: formData.iitName }]">
                      <input type="text" class="form-control" id="iitName" required v-model="formData.iitName"
                        :disabled="isSubmitted" />
                      <label for="iitName">Indian Institutes of Technology</label>
                      <p class="notice">*Fill out the name of IITs you attended. Ex. Madras</p>
                    </div>
                    <div :class="['form-group', { active: formData.iitStudyField }]">
                      <input type="text" class="form-control" id="iitStudyField" required
                        v-model="formData.iitStudyField" :disabled="isSubmitted" />
                      <label for="iitStudyField">Study field in IIT</label>
                      <p class="notice">Ex. Department of Chemistry</p>
                    </div>
                    <div :class="['form-group', { active: formData.iitGraduationDate }]">
                      <input type="text" class="form-control" id="iitGraduationDate" required
                        v-model="formData.iitGraduationDate" :disabled="isSubmitted" />
                      <label for="iitGraduationDate">(Anticipated) Month and Year of graduation</label>
                      <p class="notice">Ex. March 2016</p>
                    </div>

                    <div class="form-group full received_group">
                      <label for="">Degree Received</label>

                      <div class="check_group">
                        <div class="form-check">
                          <input class="form-check-input" type="radio" name="Received" id="receivedBachelor"
                            v-model="formData.iitDegreeReceived" value="1" :disabled="isSubmitted" />
                          <label class="form-check-label" for="receivedBachelor">
                            Bachelor
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="radio" name="Received" id="receivedMaster"
                            v-model="formData.iitDegreeReceived" value="2" :disabled="isSubmitted" />
                          <label class="form-check-label" for="receivedMaster">
                            Master
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="radio" name="Received" id="receivedPh"
                            v-model="formData.iitDegreeReceived" value="3" :disabled="isSubmitted" />
                          <label class="form-check-label" for="receivedPh">
                            Ph.D.
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="radio" name="Received" id="receivedOther"
                            v-model="formData.iitDegreeReceived" value="4" :disabled="isSubmitted" />
                          <label class="form-check-label" for="receivedOther">
                            other(specified)
                          </label>
                          <input type="text" class="form-control" id="iitDegreeOther" v-model="formData.iitDegreeOther"
                            placeholder="" :required="formData.iitDegreeReceived === '4'" :disabled="isSubmitted" />
                        </div>
                      </div>
                    </div>

                    <hr class="line_hr" />

                    <div class="form-group full check_block interest_box">
                      <label for="">2. Research focus/interest at NTHU: (Multiple Selection) Applicants must select at
                        least one focus area from the followings:</label>
                      <div class="check_group">
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interestSystemsSoftware"
                            name="interest" id="interest1" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest1">Systems Software</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interest5GCommunications"
                            name="interest" id="interest2" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest2">5G Communications</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interestMachineLearning"
                            name="interest" id="interest3" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest3">Machine Learning</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interestVRAR"
                            name="interest" id="interest4" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest4">Virtual,Augmented Reality</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interestDeepNeuralNetwork"
                            name="interest" id="interest5" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest5">Deep Neural Network</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interestDrone"
                            name="interest" id="interest6" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest6">Drone</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interestCyberSecurity"
                            name="interest" id="interest7" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest7">Cyber Security</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interestRobotics"
                            name="interest" id="interest8" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest8">Robotics,Precision Manufacturing</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interestAutonomousVehicles"
                            name="interest" id="interest9" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest9">Autonomous Vehicles, Electric Vehicles</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interest3DIC"
                            name="interest" id="interest10" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest10">3DIC, LED, OLED, Optical Systems</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.interest3DPrinting"
                            name="interest" id="interest11" :disabled="isSubmitted" />
                          <label class="form-check-label" for="interest11">3D Printing, CNC, Machine Tools</label>
                        </div>
                      </div>
                    </div>

                    <hr class="line_hr" />

                    <label class="label_title" for="">3. Prospective Advisor at NTHU: Before application, please
                      contact NTHU professor(s) who agrees to act as an advisor if
                      you are admitted.</label>

                    <div :class="['form-group', { active: formData.professorName }]">
                      <input type="text" class="form-control" id="professorName" required
                        v-model="formData.professorName" :disabled="isSubmitted" />
                      <label for="professorName">Name</label>
                    </div>

                    <div :class="[
                      'form-group',
                      { active: formData.professorDepartment },
                    ]">
                      <input type="text" class="form-control" id="professorDepartment" required
                        v-model="formData.professorDepartment" :disabled="isSubmitted" />
                      <label for="professorDepartment">Department/Institute</label>
                    </div>

                    <div :class="['form-group', { active: formData.professorEmail }]">
                      <input type="text" class="form-control" id="professorEmail" required
                        v-model="formData.professorEmail" :disabled="isSubmitted" />
                      <label for="professorEmail">E-mail</label>
                    </div>

                    <div class="form-group checkForm_group full">
                      <label for="">Does the prospective advisor agree to collaborate with
                        ITRI&NTHU Coordinated Graduate Program if you are
                        selected?</label>

                      <div class="check_group">
                        <div class="form-check">
                          <input class="form-check-input" type="radio" name="advisor" id="advisorYes"
                            v-model="formData.professorAgreeITRI" value="1" :disabled="isSubmitted" />
                          <label class="form-check-label" for="advisorYes">
                            Yes, please submit a confirmation (it could be an
                            e-mail format) from the professor
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="radio" name="advisor" id="advisorNo"
                            v-model="formData.professorAgreeITRI" value="2" :disabled="isSubmitted" />
                          <label class="form-check-label" for="advisorNo">
                            No
                          </label>
                        </div>
                      </div>
                    </div>

                    <hr class="line_hr" />

                    <div class="form-group full check_block">
                      <label for="">4. DECLARATION: I declare that:</label>
                      <div class="check_group">
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.declareIITAlumni"
                            name="declare" id="declare1" :disabled="isSubmitted" />
                          <label class="form-check-label" for="declare1">I am the alumni of the Indian Institutes of
                            Technology (IITs)</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.declareWillGraduate"
                            name="declare" id="declare2" :disabled="isSubmitted" />
                          <label class="form-check-label" for="declare2">I will graduate from IITs before January,
                            2021.</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" v-model="formData.declareInfoAccurate"
                            name="declare" id="declare3" :disabled="isSubmitted" />
                          <label class="form-check-label" for="declare3">The information I have given on this
                            application
                            is complete and accurate to the best of my knowledge.</label>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
                <div v-if="showValidationErrors && !isITRIProgramValid" class="notFilledIn_text">Not filled in</div>
              </div>
            </template>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Family Data</h3>
            </div>

            <div
              :class="['form-group full', { active: formData.fatherName, notFilledIn: showValidationErrors && !isFatherNameValid }]">
              <input type="text" class="form-control" id="fatherName" required v-model="formData.fatherName"
                :disabled="isSubmitted" />
              <label for="fatherName"><span class="required">*</span>Father Name</label>
              <div v-if="showValidationErrors && !isFatherNameValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.fatherCountryOfBirth, notFilledIn: showValidationErrors && !isFatherCountryOfBirthValid },
            ]">
              <select class="form-control" id="fatherCountryOfBirth" v-model="formData.fatherCountryOfBirth"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="fatherCountryOfBirth"><span class="required">*</span>Country of Birth</label>
              <img src="img/icon_formArrow.svg" alt="" />
              <div v-if="showValidationErrors && !isFatherCountryOfBirthValid" class="notFilledIn_text">Not filled in
              </div>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.fatherCountryOfCitizenship, notFilledIn: showValidationErrors && !isFatherCountryOfCitizenshipValid },
            ]">
              <select class="form-control" id="fatherCountryOfCitizenship" v-model="formData.fatherCountryOfCitizenship"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="fatherCountryOfCitizenship"><span class="required">*</span>Country of Citizenship</label>
              <img src="img/icon_formArrow.svg" alt="" />
              <div v-if="showValidationErrors && !isFatherCountryOfCitizenshipValid" class="notFilledIn_text">Not filled
                in</div>
            </div>

            <div
              :class="['form-group', { active: isFatherPhoneFilled, focused: isFatherPhoneFocused, notFilledIn: showValidationErrors && !isFatherPhoneValid }]">
              <div class="phone_box">
                <input type="text" class="form-control" id="fatherPhoneCountry" v-model="formData.fatherPhoneCountry"
                  @focus="isFatherPhoneFocused = true" @blur="isFatherPhoneFocused = false" required
                  placeholder="country" :disabled="isSubmitted" />
                <input type="text" class="form-control" id="fatherPhoneArea" v-model="formData.fatherPhoneArea"
                  @focus="isFatherPhoneFocused = true" @blur="isFatherPhoneFocused = false" required
                  placeholder="code-area" :disabled="isSubmitted" />
                <input type="text" class="form-control" id="fatherPhoneNumber" v-model="formData.fatherPhoneNumber"
                  @focus="isFatherPhoneFocused = true" @blur="isFatherPhoneFocused = false" required
                  placeholder="code-number" :disabled="isSubmitted" />
                <label for=""><span class="required">*</span>Phone</label>
              </div>
              <p class="notice">+Country code-Area code-number</p>
              <div v-if="showValidationErrors && !isFatherPhoneValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <hr class="line_hr" />

            <div
              :class="['form-group full', { active: formData.motherName, notFilledIn: showValidationErrors && !isMotherNameValid }]">
              <input type="text" class="form-control" id="motherName" required v-model="formData.motherName"
                :disabled="isSubmitted" />
              <label for="motherName"><span class="required">*</span>Mother Name</label>
              <div v-if="showValidationErrors && !isMotherNameValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.motherCountryOfBirth, notFilledIn: showValidationErrors && !isMotherCountryOfBirthValid },
            ]">
              <select class="form-control" id="motherCountryOfBirth" v-model="formData.motherCountryOfBirth"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="motherCountryOfBirth"><span class="required">*</span>Country of Birth</label>
              <img src="img/icon_formArrow.svg" alt="" />
              <div v-if="showValidationErrors && !isMotherCountryOfBirthValid" class="notFilledIn_text">Not filled in
              </div>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.motherCountryOfCitizenship, notFilledIn: showValidationErrors && !isMotherCountryOfCitizenshipValid },
            ]">
              <select class="form-control" id="motherCountryOfCitizenship" v-model="formData.motherCountryOfCitizenship"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="motherCountryOfCitizenship"><span class="required">*</span>Country of Citizenship</label>
              <img src="img/icon_formArrow.svg" alt="" />
              <div v-if="showValidationErrors && !isMotherCountryOfCitizenshipValid" class="notFilledIn_text">Not filled
                in</div>
            </div>

            <div
              :class="['form-group', { active: isMotherPhoneFilled, focused: isMotherPhoneFocused, notFilledIn: showValidationErrors && !isMotherPhoneValid }]">
              <div class="phone_box">
                <input type="text" class="form-control" id="motherPhoneCountry" v-model="formData.motherPhoneCountry"
                  @focus="isMotherPhoneFocused = true" @blur="isMotherPhoneFocused = false" required
                  placeholder="country" :disabled="isSubmitted" />
                <input type="text" class="form-control" id="motherPhoneArea" v-model="formData.motherPhoneArea"
                  @focus="isMotherPhoneFocused = true" @blur="isMotherPhoneFocused = false" required
                  placeholder="code-area" :disabled="isSubmitted" />
                <input type="text" class="form-control" id="motherPhoneNumber" v-model="formData.motherPhoneNumber"
                  @focus="isMotherPhoneFocused = true" @blur="isMotherPhoneFocused = false" required
                  placeholder="code-number" :disabled="isSubmitted" />
                <label for=""><span class="required">*</span>Phone</label>
              </div>
              <p class="notice">+Country code-Area code-number</p>
              <div v-if="showValidationErrors && !isMotherPhoneValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Contact Person for Emergency</h3>
            </div>

            <div
              :class="['form-group', { active: formData.emergencyName, notFilledIn: showValidationErrors && !isEmergencyNameValid }]">
              <input type="text" class="form-control" id="emergencyName" required v-model="formData.emergencyName"
                :disabled="isSubmitted" />
              <label for="emergencyName"><span class="required">*</span>Name</label>
              <div v-if="showValidationErrors && !isEmergencyNameValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div :class="[
              'form-group',
              { active: formData.emergencyRelationship, notFilledIn: showValidationErrors && !isEmergencyRelationshipValid },
            ]">
              <input type="text" class="form-control" id="emergencyRelationship" required
                v-model="formData.emergencyRelationship" :disabled="isSubmitted" />
              <label for="emergencyRelationship"><span class="required">*</span>Relationship</label>
              <div v-if="showValidationErrors && !isEmergencyRelationshipValid" class="notFilledIn_text">Not filled in
              </div>
            </div>

            <div
              :class="['form-group', { active: formData.emergencyEmail, notFilledIn: showValidationErrors && !isEmergencyEmailValid }]">
              <input type="text" class="form-control" id="emergencyEmail" required v-model="formData.emergencyEmail"
                :disabled="isSubmitted" />
              <label for="emergencyEmail"><span class="required">*</span>Email</label>
              <div v-if="showValidationErrors && !isEmergencyEmailValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group', { active: isEmergencyPhoneFilled, focused: isEmergencyPhoneFocused, notFilledIn: showValidationErrors && !isEmergencyPhoneValid }]">
              <div class="phone_box">
                <input type="text" class="form-control" id="emergencyPhoneCountry"
                  v-model="formData.emergencyPhoneCountry" @focus="isEmergencyPhoneFocused = true"
                  @blur="isEmergencyPhoneFocused = false" required placeholder="country" :disabled="isSubmitted" />
                <input type="text" class="form-control" id="emergencyPhoneArea" v-model="formData.emergencyPhoneArea"
                  @focus="isEmergencyPhoneFocused = true" @blur="isEmergencyPhoneFocused = false" required
                  placeholder="code-area" :disabled="isSubmitted" />
                <input type="text" class="form-control" id="emergencyPhoneNumber"
                  v-model="formData.emergencyPhoneNumber" @focus="isEmergencyPhoneFocused = true"
                  @blur="isEmergencyPhoneFocused = false" required placeholder="code-number" :disabled="isSubmitted" />
                <label for=""><span class="required">*</span>Phone</label>
              </div>
              <p class="notice">+Country code-Area code-number</p>
              <div v-if="showValidationErrors && !isEmergencyPhoneValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group', { active: formData.emergencyStreet, notFilledIn: showValidationErrors && !isEmergencyStreetValid }]">
              <input type="text" class="form-control" id="emergencyStreet" required v-model="formData.emergencyStreet"
                :disabled="isSubmitted" />
              <label for="emergencyStreet"><span class="required">*</span>Street</label>
              <div v-if="showValidationErrors && !isEmergencyStreetValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group', { active: formData.emergencyCity, notFilledIn: showValidationErrors && !isEmergencyCityValid }]">
              <input type="text" class="form-control" id="emergencyCity" required v-model="formData.emergencyCity"
                :disabled="isSubmitted" />
              <label for="emergencyCity"><span class="required">*</span>City</label>
              <div v-if="showValidationErrors && !isEmergencyCityValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.emergencyCountry, notFilledIn: showValidationErrors && !isEmergencyCountryValid },
            ]">
              <select class="form-control" id="emergencyCountry" v-model="formData.emergencyCountry"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="emergencyCountry"><span class="required">*</span>Country</label>
              <img src="img/icon_formArrow.svg" alt="" />
              <div v-if="showValidationErrors && !isEmergencyCountryValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Academic Background (High School)</h3>
            </div>

            <div :class="['form-group', { active: formData.highSchoolName }]">
              <input type="text" class="form-control" id="highSchoolName" required v-model="formData.highSchoolName"
                :disabled="isSubmitted" />
              <label for="highSchoolName">Name of School</label>
            </div>

            <div :class="['form-group', { active: formData.highSchoolStreet }]">
              <input type="text" class="form-control" id="highSchoolStreet" required v-model="formData.highSchoolStreet"
                :disabled="isSubmitted" />
              <label for="highSchoolStreet">Street of School</label>
            </div>

            <div :class="['form-group', { active: formData.highSchoolCity }]">
              <input type="text" class="form-control" id="highSchoolCity" required v-model="formData.highSchoolCity"
                :disabled="isSubmitted" />
              <label for="highSchoolCity">City of School </label>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.highSchoolCountry },
            ]">
              <select class="form-control" id="highSchoolCountry" v-model="formData.highSchoolCountry"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="highSchoolCountry">Country of School</label>
              <img src="img/icon_formArrow.svg" alt="" />
            </div>

            <div :class="['form-group', { active: formData.highSchoolAttended }]">
              <input type="date" class="form-control" id="highSchoolAttended" placeholder=""
                v-model="formData.highSchoolAttended" :disabled="isSubmitted" />
              <label for="highSchoolAttended">Dates Attended</label>

              <p class="notice">The date when you enrolled</p>
            </div>

            <div :class="['form-group', { active: formData.highSchoolGraduation }]">
              <input type="date" class="form-control" id="highSchoolGraduation" placeholder=""
                v-model="formData.highSchoolGraduation" :disabled="isSubmitted" />
              <label for="highSchoolGraduation">Date of Graduation</label>

              <p class="notice">The date when you graduated</p>
            </div>

            <div :class="['form-group', { active: formData.highSchoolReceive }]">
              <input type="text" class="form-control" id="highSchoolReceive" required
                v-model="formData.highSchoolReceive" :disabled="isSubmitted" />
              <label for="highSchoolReceive">Degree Received or Will Receive</label>

              <p class="notice">(e.g. Bachelor )</p>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>
                Academic Background (Undergraduate):Fill out if applicable
              </h3>
            </div>

            <div :class="['form-group', { active: formData.undergraduateName }]">
              <input type="text" class="form-control" id="undergraduateName" required
                v-model="formData.undergraduateName" :disabled="isSubmitted" />
              <label for="undergraduateName">Name of School</label>
            </div>

            <div :class="['form-group', { active: formData.undergraduateStreet }]">
              <input type="text" class="form-control" id="undergraduateStreet" required
                v-model="formData.undergraduateStreet" :disabled="isSubmitted" />
              <label for="undergraduateStreet">Street of School</label>
            </div>

            <div :class="['form-group', { active: formData.undergraduateCity }]">
              <input type="text" class="form-control" id="undergraduateCity" required
                v-model="formData.undergraduateCity" :disabled="isSubmitted" />
              <label for="undergraduateCity">City of School </label>
            </div>

            <div :class="['form-group arrow_bg', { active: formData.undergraduateCountry }]">
              <select class="form-control" id="undergraduateCountry" v-model="formData.undergraduateCountry"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="undergraduateCountry">Country of School</label>
              <img src="img/icon_formArrow.svg" alt="" />
            </div>

            <div :class="[
              'form-group',
              { active: formData.undergraduateAttended },
            ]">
              <input type="date" class="form-control" id="undergraduateAttended" placeholder=""
                v-model="formData.undergraduateAttended" :disabled="isSubmitted" />
              <label for="undergraduateAttended">Dates Attended</label>

              <p class="notice">The date when you enrolled</p>
            </div>

            <div :class="[
              'form-group',
              { active: formData.undergraduateGraduation },
            ]">
              <input type="date" class="form-control" id="undergraduateGraduation" placeholder=""
                v-model="formData.undergraduateGraduation" :disabled="isSubmitted" />
              <label for="undergraduateGraduation">Date of Graduation</label>

              <p class="notice">The date when you graduated</p>
            </div>

            <div :class="['form-group', { active: formData.undergraduateReceive }]">
              <input type="text" class="form-control" id="undergraduateReceive" required
                v-model="formData.undergraduateReceive" :disabled="isSubmitted" />
              <label for="undergraduateReceive">Degree Received or Will Receive</label>

              <p class="notice">(e.g. Bachelor )</p>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Academic Background (Graduate):Fill out if applicable</h3>
            </div>

            <div :class="['form-group', { active: formData.graduateName }]">
              <input type="text" class="form-control" id="graduateName" required v-model="formData.graduateName"
                :disabled="isSubmitted" />
              <label for="graduateName">Name of School</label>
            </div>

            <div :class="['form-group', { active: formData.graduateStreet }]">
              <input type="text" class="form-control" id="graduateStreet" required v-model="formData.graduateStreet"
                :disabled="isSubmitted" />
              <label for="graduateStreet">Street of School</label>
            </div>

            <div :class="['form-group', { active: formData.graduateCity }]">
              <input type="text" class="form-control" id="graduateCity" required v-model="formData.graduateCity"
                :disabled="isSubmitted" />
              <label for="graduateCity">City of School </label>
            </div>

            <div :class="[
              'form-group arrow_bg',
              { active: formData.graduateCountry },
            ]">
              <select class="form-control" id="graduateCountry" v-model="formData.graduateCountry"
                :disabled="isSubmitted">
                <option value="" disabled selected hidden></option>
                <option v-for="country in availableCountries" :key="country.cId" :value="country.code">
                  {{ country.name_EN }}
                </option>
              </select>
              <label for="graduateCountry">Country of School</label>
              <img src="img/icon_formArrow.svg" alt="" />
            </div>

            <div :class="['form-group', { active: formData.graduateAttended }]">
              <input type="date" class="form-control" id="graduateAttended" placeholder=""
                v-model="formData.graduateAttended" :disabled="isSubmitted" />
              <label for="graduateAttended">Dates Attended</label>

              <p class="notice">The date when you enrolled</p>
            </div>

            <div :class="['form-group', { active: formData.graduateGraduation }]">
              <input type="date" class="form-control" id="graduateGraduation" placeholder=""
                v-model="formData.graduateGraduation" :disabled="isSubmitted" />
              <label for="graduateGraduation">Date of Graduation</label>

              <p class="notice">The date when you graduated</p>
            </div>

            <div :class="['form-group', { active: formData.graduateReceive }]">
              <input type="text" class="form-control" id="graduateReceive" required v-model="formData.graduateReceive"
                :disabled="isSubmitted" />
              <label for="graduateReceive">Degree Received or Will Receive</label>

              <p class="notice">(e.g. Bachelor )</p>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Studying in Taiwan</h3>
            </div>

            <div
              :class="['form-group check_block full', { notFilledIn: showValidationErrors && !isStudiedInTaiwanValid }]">
              <label for=""><span class="required">*</span>Have you been studied in Taiwan
                (including degree, short term, exchange, Mandarin program
                etc.)</label>

              <div class="check_group financialSupport_box">
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="studiedInTaiwan" id="studiedInTaiwan1"
                    v-model="formData.studiedInTaiwan" value="1" :disabled="isSubmitted" />
                  <label class="form-check-label" for="studiedInTaiwan1">
                    Yes
                  </label>

                  <input type="text" class="form-control" id="studiedInTaiwanDetail"
                    v-model="formData.studiedInTaiwanDetail" placeholder="School name and type of program "
                    :required="formData.studiedInTaiwan === '1'" :disabled="isSubmitted" />
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="studiedInTaiwan" id="studiedInTaiwan2"
                    v-model="formData.studiedInTaiwan" value="2" :disabled="isSubmitted" />
                  <label class="form-check-label" for="studiedInTaiwan2">
                    No
                  </label>
                </div>
              </div>
              <div v-if="showValidationErrors && !isStudiedInTaiwanValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Language Proficiency</h3>
            </div>

            <div
              :class="['form-group full language_box', { notFilledIn: showValidationErrors && !isLanguageEnglishValid }]">
              <label for=""><span class="required">*</span>English</label>

              <div class="check_group">
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageEnglish" id="languageEnglish1"
                    v-model="formData.languageEnglish" value="5" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageEnglish1">
                    Fluent
                  </label>
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageEnglish" id="languageEnglish2"
                    v-model="formData.languageEnglish" value="4" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageEnglish2">
                    Good
                  </label>
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageEnglish" id="languageEnglish3"
                    v-model="formData.languageEnglish" value="3" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageEnglish3">
                    Moderate
                  </label>
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageEnglish" id="languageEnglish4"
                    v-model="formData.languageEnglish" value="2" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageEnglish4">
                    Limited
                  </label>
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageEnglish" id="languageEnglish5"
                    v-model="formData.languageEnglish" value="1" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageEnglish5">
                    None
                  </label>
                </div>
              </div>
              <div v-if="showValidationErrors && !isLanguageEnglishValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group full language_box', { notFilledIn: showValidationErrors && !isLanguageMandarinValid }]">
              <label for=""><span class="required">*</span>Mandarin</label>

              <div class="check_group">
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageMandarin" id="languageMandarin1"
                    v-model="formData.languageMandarin" value="5" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageMandarin1">
                    Fluent
                  </label>
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageMandarin" id="languageMandarin2"
                    v-model="formData.languageMandarin" value="4" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageMandarin2">
                    Good
                  </label>
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageMandarin" id="languageMandarin3"
                    v-model="formData.languageMandarin" value="3" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageMandarin3">
                    Moderate
                  </label>
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageMandarin" id="languageMandarin4"
                    v-model="formData.languageMandarin" value="2" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageMandarin4">
                    Limited
                  </label>
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageMandarin" id="languageMandarin5"
                    v-model="formData.languageMandarin" value="1" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageMandarin5">
                    None
                  </label>
                </div>
              </div>
              <div v-if="showValidationErrors && !isLanguageMandarinValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div class="form-group full language_box">
              <label for="">Other
                <input type="text" class="form-control" id="languageOtherName" v-model="formData.languageOtherName"
                  placeholder="please specify " :disabled="isSubmitted" />
              </label>
              <div class="check_group">
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageOther" id="languageOther1"
                    v-model="formData.languageOtherLevel" value="5" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageOther1">Fluent</label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageOther" id="languageOther2"
                    v-model="formData.languageOtherLevel" value="4" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageOther2">Good</label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageOther" id="languageOther3"
                    v-model="formData.languageOtherLevel" value="3" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageOther3">Moderate</label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageOther" id="languageOther4"
                    v-model="formData.languageOtherLevel" value="2" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageOther4">Limited</label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="languageOther" id="languageOther5"
                    v-model="formData.languageOtherLevel" value="1" :disabled="isSubmitted" />
                  <label class="form-check-label" for="languageOther5">None</label>
                </div>
              </div>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Other Information</h3>
            </div>

            <div
              :class="['form-group check_block full', { notFilledIn: showValidationErrors && !isHasTaiwanARCValid }]">
              <label for=""><span class="required">*</span>Do you have Taiwan ARC (Alien
                Resident Certificate)? If yes, upload a copy along with your
                passport.</label>

              <div class="check_group financialSupport_box">
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="taiwanARC" id="taiwanARC1"
                    v-model="formData.hasTaiwanARC" value="1" :disabled="isSubmitted" />
                  <label class="form-check-label" for="taiwanARC1"> Yes </label>

                  <input type="text" class="form-control" id="taiwanARCNumber" v-model="formData.taiwanARCNumber"
                    placeholder="ARC NO.(Please submit the copy along with your passport)"
                    :required="formData.hasTaiwanARC === '1'" :disabled="isSubmitted" />
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="taiwanARC" id="taiwanARC2"
                    v-model="formData.hasTaiwanARC" value="2" :disabled="isSubmitted" />
                  <label class="form-check-label" for="taiwanARC2"> No </label>
                </div>
              </div>
              <div v-if="showValidationErrors && !isHasTaiwanARCValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <div
              :class="['form-group check_block full', { notFilledIn: showValidationErrors && !isHasTaiwanAPRCValid }]">
              <label for=""><span class="required">*</span>Do you have Taiwan APRC(Alien
                Permanent Resident Certificate) If yes, upload a copy along with
                your passport.</label>

              <div class="check_group financialSupport_box">
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="taiwanAPRC" id="taiwanAPRC1"
                    v-model="formData.hasTaiwanAPRC" value="1" :disabled="isSubmitted" />
                  <label class="form-check-label" for="taiwanAPRC1">
                    Yes
                  </label>

                  <input type="text" class="form-control" id="taiwanAPRCNumber" v-model="formData.taiwanAPRCNumber"
                    placeholder="APRC NO. " :required="formData.hasTaiwanAPRC === '1'" :disabled="isSubmitted" />
                </div>

                <div class="form-check">
                  <input class="form-check-input" type="radio" name="taiwanAPRC" id="taiwanAPRC2"
                    v-model="formData.hasTaiwanAPRC" value="2" :disabled="isSubmitted" />
                  <label class="form-check-label" for="taiwanAPRC2"> No </label>
                </div>
              </div>
              <div v-if="showValidationErrors && !isHasTaiwanAPRCValid" class="notFilledIn_text">Not filled in</div>
            </div>

            <hr class="line_hr" />

            <div class="title_box">
              <h3>Message Source</h3>
            </div>

            <div :class="['form-group full', { notFilledIn: showValidationErrors && !isMessageSourceValid }]">
              <label for=""><span class="required">*</span>How did you find out about
                National Tsing Hua University?(Multiple)</label>

              <div class="check_group financialSupport_box messageSource_box">
                <div v-for="source in availableMessageSources" :key="source.msId"
                  :class="['form-check', { 'other_box': source.isOther }]">
                  <input class="form-check-input" type="checkbox" :value="source.msId"
                    :id="'messageSource_' + source.msId" v-model="selectedMessageSources[source.msId].selected"
                    :disabled="isSubmitted" />
                  <label class="form-check-label" :for="'messageSource_' + source.msId">
                    {{ source.name_EN || source.name }}
                  </label>

                  <!-- 如果是其他選項，顯示其他文字輸入框 -->
                  <input v-if="source.isOther && selectedMessageSources[source.msId].selected" type="text"
                    class="form-control" :id="'messageSource_other_' + source.msId" placeholder="(please specify)"
                    v-model="selectedMessageSources[source.msId].otherText" required />
                </div>
              </div>
              <div v-if="showValidationErrors && !isMessageSourceValid" class="notFilledIn_text">Not filled in</div>
            </div>
          </div>
          <!-- End of v-else content -->

          <div v-if="!isLoading" class="btn_box">
            <button class="btn" @click="gotoNext" :disabled="isSubmitted">Next</button>
            <button class="btn" @click="saveApplication" :disabled="isSaving || isSubmitted">
              {{ isSaving ? 'Saving...' : (isSubmitted ? 'Submitted (Read-Only)' : 'Save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>
<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import applyStudentService from "@/services/applyStudent.js";
import Swal from "sweetalert2";

import { driver } from "driver.js";

export default {
  name: "Form",
  components: {
    Header,
    Footer,
  },
  data() {
    return {
      // 進度步驟
      currentStep: 0, // 表示第1步(index 0)
      progressItems: [
        { to: "/Apply/1", title: "Fill out online application form" },
        { to: "/Apply/2", title: "Apply for a program" },
        {
          to: "/Apply/3",
          title:
            "Fill out Recommenders (Do Not Fill Out Without Notifying Your Recommenders in Advance.)",
        },
        { to: "/Apply/4", title: "Upload Required Documents 1" },
        { to: "/Apply/5", title: "Upload Required Documents 2" },
        { to: "/Apply/Check", title: "Review & Submit" },
        { to: "/Apply/Pay", title: "Pay Application Fee" },
      ],

      // 使用說明導覽
      driverObj: null,
      arr: [
        {
          element: "#step1",
          popover: {
            description:
              "Fields marked with an asterisk <span style='font-weight: 800;font-size: 1.5rem;color: #F44A6C;'>*</span>are required.",
            side: "bottom",
            align: "start",
          },
        },
        {
          element: "#step2",
          popover: {
            description:
              " Please print as it appears on your passport name(s) and do not use abbreviations. Fill out your name (the beginning letter of each word) in capital letters. Ex. Andrew Shu ",
            side: "bottom",
            align: "start",
          },
        },
        {
          element: "#step3",
          popover: {
            description: "(Fill out in Chinese only if applicable)",
            side: "bottom",
            align: "start",
          },
        },
      ],

      // 完整的申請表單資料結構
      formData: {
        asId: -1,

        // Personal Data (個人資料)
        nameFirst: "",
        nameMiddle: "",
        nameLast: "",
        chineseName: "",
        birth: "", // dateOfBirth in API
        contactEmail: "",
        gender: 0, // 0:未設定, 1:男, 2:女
        maritalStatus: 0, // 0:未設定, 1:未婚, 2:已婚
        nativeLanguage: "",
        photo: "", // 大頭照路徑

        // Permanent Address (永久地址)
        street: "", // permStreet in API
        city: "", // permCity in API
        country: "", // permCountry in API
        zipCode: "", // permZipCode in API
        phoneCountry: "", // permPhoneCountry in API
        phoneArea: "", // permPhoneArea in API
        phoneNumber: "", // permPhoneNumber in API

        // Mailing Address (郵寄地址)
        mailingStreet: "", // mailStreet in API
        mailingCity: "", // mailCity in API
        mailingCountry: "", // mailCountry in API
        mailingZipCode: "", // mailZipCode in API
        mailingPhoneCountry: "", // mailPhoneCountry in API
        mailingPhoneArea: "", // mailPhoneArea in API
        mailingPhoneNumber: "", // mailPhoneNumber in API
        sameAsPermanent: false, // SameAsPermanent in API

        // Citizenship & Passport Information (國籍與護照資訊)
        countryOfBirth: "",
        countryOfCitizenship: "",
        passportNumber: "",

        // Financial Resources (財務資源) - will be saved as JSON
        financial: [], // Array of selected finance items

        // NTHU Scholarship Application (清華獎學金申請)
        scholarshipApplication: 0, // 0:未設定, 1:是, 2:否

        // ITRI & NTHU Program (工研院合作計畫)
        isITRIProgram: 0, // 0:未設定, 1:是, 2:否
        iitName: "",
        iitStudyField: "",
        iitGraduationDate: "",
        iitDegreeReceived: 0, // 0:未設定, 1:Bachelor, 2:Master, 3:PhD, 4:Other
        iitDegreeOther: "",

        // Research Interest (研究興趣) - multiple selections
        interestSystemsSoftware: false,
        interest5GCommunications: false,
        interestMachineLearning: false,
        interestVRAR: false,
        interestDeepNeuralNetwork: false,
        interestDrone: false,
        interestCyberSecurity: false,
        interestRobotics: false,
        interestAutonomousVehicles: false,
        interest3DIC: false,
        interest3DPrinting: false,

        // Prospective Advisor (預期指導教授)
        professorName: "",
        professorDepartment: "",
        professorEmail: "",
        professorAgreeITRI: 0, // 0:未設定, 1:是, 2:否

        // Declaration (聲明) - IIT相關
        declareIITAlumni: false,
        declareWillGraduate: false,
        declareInfoAccurate: false,

        // Family Data - Father (父親資料)
        fatherName: "",
        fatherCountryOfBirth: "",
        fatherCountryOfCitizenship: "",
        fatherPhoneCountry: "",
        fatherPhoneArea: "",
        fatherPhoneNumber: "",

        // Family Data - Mother (母親資料)
        motherName: "",
        motherCountryOfBirth: "",
        motherCountryOfCitizenship: "",
        motherPhoneCountry: "",
        motherPhoneArea: "",
        motherPhoneNumber: "",

        // Emergency Contact (緊急聯絡人)
        emergencyName: "",
        emergencyRelationship: "",
        emergencyEmail: "",
        emergencyPhoneCountry: "",
        emergencyPhoneArea: "",
        emergencyPhoneNumber: "",
        emergencyStreet: "",
        emergencyCity: "",
        emergencyCountry: "",

        // Academic Background - High School (高中學歷)
        highSchoolName: "",
        highSchoolStreet: "",
        highSchoolCity: "",
        highSchoolCountry: "",
        highSchoolAttended: "",
        highSchoolGraduation: "",
        highSchoolReceive: "",

        // Academic Background - Undergraduate (大學學歷)
        undergraduateName: "",
        undergraduateStreet: "",
        undergraduateCity: "",
        undergraduateCountry: "",
        undergraduateAttended: "",
        undergraduateGraduation: "",
        undergraduateReceive: "",

        // Academic Background - Graduate (研究所學歷)
        graduateName: "",
        graduateStreet: "",
        graduateCity: "",
        graduateCountry: "",
        graduateAttended: "",
        graduateGraduation: "",
        graduateReceive: "",

        // Studying in Taiwan (曾在台灣就讀)
        studiedInTaiwan: 0, // 0:未設定, 1:是, 2:否
        studiedInTaiwanDetail: "",

        // Language Proficiency (語言能力)
        languageEnglish: 0, // 0:未設定, 5:Fluent, 4:Good, 3:Moderate, 2:Limited, 1:None
        languageMandarin: 0,
        languageOther: false,
        languageOtherName: "",
        languageOtherLevel: 0,

        // Other Information (其他資訊)
        hasTaiwanARC: 0, // 0:未設定, 1:是, 2:否
        taiwanARCNumber: "",
        hasTaiwanAPRC: 0,
        taiwanAPRCNumber: "",

        // Message Source (訊息來源) - will be saved as JSON
        messageSource: [], // Array of selected message sources
        //applyPrograms: [],
      },

      // 焦點狀態
      isEnglishNameFocused: false,
      isPhoneFocused: false,
      isMailingPhoneFocused: false,
      isFatherPhoneFocused: false,
      isMotherPhoneFocused: false,
      isEmergencyPhoneFocused: false,

      // 地址複製
      sameAsPermanent: false,

      // ITRI Program 展開
      IITsAnswer: null,

      // 動態選項資料
      availableFinances: [], // 財務來源選項
      availableMessageSources: [], // 訊息來源選項
      availableCountries: [], // 國家列表選項
      availableScholarships: [], // 可申請的獎學金清單

      // 財務來源相關
      selectedFinances: {}, // { fid: { selected: bool, otherText: '', scholarshipText: '' } }

      // 訊息來源相關
      selectedMessageSources: {}, // { msid: { selected: bool, otherText: '' } }

      // 獎學金申請相關
      scholarshipSelections: {}, // { sId: { isApply: bool, fileUrl: string, fileName: string } }

      // 載入狀態
      isLoading: false,
      isSaving: false,

      // 申請狀態
      isSubmitted: false, // 申請是否已提交

      // 大頭照相關
      previewUrl: "", // 圖片預覽 URL
      isUploadingPhoto: false, // 照片上傳狀態

      // 步驟內容
      stepContent: {
        step: 1,
        title: 'Step 1',
        content: '',
        applyInfo: null
      },

      // 驗證狀態
      showValidationErrors: false, // 控制是否顯示驗證錯誤樣式

      // 訂單資訊
      hasOrder: false, // 是否已建立訂單
      isPaid: false, // 是否已付款 (status === 1)

      // 變動追蹤
      hasUnsavedChanges: false, // 是否有未儲存的變動
      initialFormData: null, // 初始載入的表單資料（用於比對）
      initialSelectedFinances: {}, // 初始財務來源選擇
      initialSelectedMessageSources: {}, // 初始訊息來源選擇
      initialScholarshipSelections: {}, // 初始獎學金選擇

      aId: null, // 申請人ID
    };
  },

  computed: {
    // 統整英文名字欄位
    isEnglishNameFilled() {
      return (
        this.formData.nameFirst ||
        this.formData.nameMiddle ||
        this.formData.nameLast
      );
    },

    // 統整Permanent Address手機欄位
    isPhoneFilled() {
      return (
        this.formData.phoneCountry ||
        this.formData.phoneArea ||
        this.formData.phoneNumber
      );
    },

    // 統整Mailing Address手機欄位
    isMailingPhoneFilled() {
      return (
        this.formData.mailingPhoneCountry ||
        this.formData.mailingPhoneArea ||
        this.formData.mailingPhoneNumber
      );
    },

    // 確認地址填寫完整才可以複製
    canCopyAddress() {
      const f = this.formData;
      return (
        f.street &&
        f.city &&
        f.country &&
        f.zipCode &&
        f.phoneCountry &&
        f.phoneArea &&
        f.phoneNumber
      );
    },

    // 統整Father手機欄位
    isFatherPhoneFilled() {
      return (
        this.formData.fatherPhoneCountry ||
        this.formData.fatherPhoneArea ||
        this.formData.fatherPhoneNumber
      );
    },

    // 統整Mother手機欄位
    isMotherPhoneFilled() {
      return (
        this.formData.motherPhoneCountry ||
        this.formData.motherPhoneArea ||
        this.formData.motherPhoneNumber
      );
    },

    // 統整Emergency手機欄位
    isEmergencyPhoneFilled() {
      return (
        this.formData.emergencyPhoneCountry ||
        this.formData.emergencyPhoneArea ||
        this.formData.emergencyPhoneNumber
      );
    },

    // ===== Required Field Validation Computed Properties =====
    // Personal Data
    isNameFirstValid() {
      return this.formData.nameFirst && this.formData.nameFirst.trim();
    },
    isNameLastValid() {
      return this.formData.nameLast && this.formData.nameLast.trim();
    },
    isBirthValid() {
      return this.formData.birth && this.formData.birth.trim();
    },
    isContactEmailValid() {
      return this.formData.contactEmail && this.formData.contactEmail.trim();
    },
    isGenderValid() {
      return this.formData.gender && this.formData.gender !== 0;
    },
    isNativeLanguageValid() {
      return this.formData.nativeLanguage && this.formData.nativeLanguage.trim();
    },

    // Permanent Address
    isStreetValid() {
      return this.formData.street && this.formData.street.trim();
    },
    isCityValid() {
      return this.formData.city && this.formData.city.trim();
    },
    isCountryValid() {
      return this.formData.country && this.formData.country.trim();
    },
    isZipCodeValid() {
      return this.formData.zipCode && this.formData.zipCode.trim();
    },
    isPhoneValid() {
      return (
        this.formData.phoneCountry && this.formData.phoneCountry.trim() &&
        this.formData.phoneArea && this.formData.phoneArea.trim() &&
        this.formData.phoneNumber && this.formData.phoneNumber.trim()
      );
    },

    // Mailing Address
    isMailingStreetValid() {
      return this.formData.mailingStreet && this.formData.mailingStreet.trim();
    },
    isMailingCityValid() {
      return this.formData.mailingCity && this.formData.mailingCity.trim();
    },
    isMailingCountryValid() {
      return this.formData.mailingCountry && this.formData.mailingCountry.trim();
    },
    isMailingZipCodeValid() {
      return this.formData.mailingZipCode && this.formData.mailingZipCode.trim();
    },
    isMailingPhoneValid() {
      return (
        this.formData.mailingPhoneCountry && this.formData.mailingPhoneCountry.trim() &&
        this.formData.mailingPhoneArea && this.formData.mailingPhoneArea.trim() &&
        this.formData.mailingPhoneNumber && this.formData.mailingPhoneNumber.trim()
      );
    },

    // Citizenship & Passport
    isCountryOfBirthValid() {
      return this.formData.countryOfBirth && this.formData.countryOfBirth.trim();
    },
    isCountryOfCitizenshipValid() {
      return this.formData.countryOfCitizenship && this.formData.countryOfCitizenship.trim();
    },

    // Financial Resources
    isFinancialValid() {
      return Object.values(this.selectedFinances).some(f => f.selected);
    },

    // ITRI Program
    isITRIProgramValid() {
      //return this.formData.isITRIProgram && this.formData.isITRIProgram !== 0;
      return true; // 非必填
    },

    // Father
    isFatherNameValid() {
      return this.formData.fatherName && this.formData.fatherName.trim();
    },
    isFatherCountryOfBirthValid() {
      return this.formData.fatherCountryOfBirth && this.formData.fatherCountryOfBirth.trim();
    },
    isFatherCountryOfCitizenshipValid() {
      return this.formData.fatherCountryOfCitizenship && this.formData.fatherCountryOfCitizenship.trim();
    },
    isFatherPhoneValid() {
      return (
        this.formData.fatherPhoneCountry && this.formData.fatherPhoneCountry.trim() &&
        this.formData.fatherPhoneArea && this.formData.fatherPhoneArea.trim() &&
        this.formData.fatherPhoneNumber && this.formData.fatherPhoneNumber.trim()
      );
    },

    // Mother
    isMotherNameValid() {
      return this.formData.motherName && this.formData.motherName.trim();
    },
    isMotherCountryOfBirthValid() {
      return this.formData.motherCountryOfBirth && this.formData.motherCountryOfBirth.trim();
    },
    isMotherCountryOfCitizenshipValid() {
      return this.formData.motherCountryOfCitizenship && this.formData.motherCountryOfCitizenship.trim();
    },
    isMotherPhoneValid() {
      return (
        this.formData.motherPhoneCountry && this.formData.motherPhoneCountry.trim() &&
        this.formData.motherPhoneArea && this.formData.motherPhoneArea.trim() &&
        this.formData.motherPhoneNumber && this.formData.motherPhoneNumber.trim()
      );
    },

    // Emergency Contact
    isEmergencyNameValid() {
      return this.formData.emergencyName && this.formData.emergencyName.trim();
    },
    isEmergencyRelationshipValid() {
      return this.formData.emergencyRelationship && this.formData.emergencyRelationship.trim();
    },
    isEmergencyEmailValid() {
      return this.formData.emergencyEmail && this.formData.emergencyEmail.trim();
    },
    isEmergencyPhoneValid() {
      return (
        this.formData.emergencyPhoneCountry && this.formData.emergencyPhoneCountry.trim() &&
        this.formData.emergencyPhoneArea && this.formData.emergencyPhoneArea.trim() &&
        this.formData.emergencyPhoneNumber && this.formData.emergencyPhoneNumber.trim()
      );
    },
    isEmergencyStreetValid() {
      return this.formData.emergencyStreet && this.formData.emergencyStreet.trim();
    },
    isEmergencyCityValid() {
      return this.formData.emergencyCity && this.formData.emergencyCity.trim();
    },
    isEmergencyCountryValid() {
      return this.formData.emergencyCountry && this.formData.emergencyCountry.trim();
    },

    // Studying in Taiwan
    isStudiedInTaiwanValid() {
      return this.formData.studiedInTaiwan && this.formData.studiedInTaiwan !== 0;
    },

    // Language Proficiency
    isLanguageEnglishValid() {
      return this.formData.languageEnglish && this.formData.languageEnglish !== 0;
    },
    isLanguageMandarinValid() {
      return this.formData.languageMandarin && this.formData.languageMandarin !== 0;
    },

    // Other Information
    isHasTaiwanARCValid() {
      return this.formData.hasTaiwanARC && this.formData.hasTaiwanARC !== 0;
    },
    isHasTaiwanAPRCValid() {
      return this.formData.hasTaiwanAPRC && this.formData.hasTaiwanAPRC !== 0;
    },

    // Message Source
    isMessageSourceValid() {
      return Object.values(this.selectedMessageSources).some(s => s.selected);
    },
  },

  watch: {
    sameAsPermanent(val) {
      if (val) {
        // ✅ 將 Permanent Address 欄位複製到 Mailing Address
        this.formData.mailingStreet = this.formData.street;
        this.formData.mailingCity = this.formData.city;
        this.formData.mailingCountry = this.formData.country;
        this.formData.mailingZipCode = this.formData.zipCode;
        this.formData.mailingPhoneCountry = this.formData.phoneCountry;
        this.formData.mailingPhoneArea = this.formData.phoneArea;
        this.formData.mailingPhoneNumber = this.formData.phoneNumber;
      } else {
        // ✅ 如果取消勾選,也可以清空 mailing 欄位(可選)
        this.formData.mailingStreet = "";
        this.formData.mailingCity = "";
        this.formData.mailingCountry = "";
        this.formData.mailingZipCode = "";
        this.formData.mailingPhoneCountry = "";
        this.formData.mailingPhoneArea = "";
        this.formData.mailingPhoneNumber = "";
      }
    },

    // 監控國籍變更，重新載入獎學金清單
    'formData.country': {
      handler(newCountry) {
        this.loadAvailableScholarships(newCountry);
      }
    },

    // 監控表單資料變動
    formData: {
      handler() {
        this.checkForChanges();
      },
      deep: true
    },

    // 監控財務來源選擇變動
    selectedFinances: {
      handler() {
        this.checkForChanges();
      },
      deep: true
    },

    // 監控訊息來源選擇變動
    selectedMessageSources: {
      handler() {
        this.checkForChanges();
      },
      deep: true
    },

    // 監控獎學金選擇變動
    scholarshipSelections: {
      handler() {
        this.checkForChanges();
      },
      deep: true
    },

    // 監控 isSubmitted 狀態並禁用所有表單元素
    isSubmitted: {
      handler(newValue) {
        this.$nextTick(() => {
          this.toggleFormElements(newValue);
        });
      },
      immediate: false
    }
  },

  async mounted() {
    window.scrollTo(0, 0);
    await this.initializeForm();

    // 設置路由守衛
    this.setupBeforeUnloadHandler();

    // 初始化時檢查並禁用表單元素（如果已提交）
    this.$nextTick(() => {
      if (this.isSubmitted) {
        this.toggleFormElements(true);
      }
    });
  },

  beforeRouteLeave(to, from, next) {
    // 當要離開此頁面時檢查是否有未儲存的變動
    if (!this.hasUnsavedChanges) {
      next();
      return;
    }

    // 使用 Promise 處理非同步操作
    Swal.fire({
      title: 'Unsaved Changes',
      text: 'You have unsaved changes. What would you like to do?',
      icon: 'warning',
      //showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Save and Leave',
      denyButtonText: 'Leave without Saving',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#580d63',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        // 儲存並離開
        const saveSuccess = await this.saveApplicationSilently();
        if (saveSuccess) {
          this.hasUnsavedChanges = false;
          next();
        } else {
          next(false);
        }
      } else if (result.isDenied) {
        // 直接離開
        this.hasUnsavedChanges = false;
        next();
      } else {
        // 取消 - 阻止導航
        next(false);
      }
    }).catch((error) => {
      console.error("Error in beforeRouteLeave:", error);
      next(false);
    });
  },

  beforeUnmount() {
    // 移除 beforeunload 事件監聽
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },

  methods: {
    /**
     * 初始化表單 - 載入選項和現有申請資料
     */
    async initializeForm() {
      this.isLoading = true;
      try {
        const checkResult = await applyStudentService.checkUserApplication();
        if (!checkResult.success || !checkResult.data ||
          !checkResult.data?.isReview ||
          checkResult.data?.isAgree != 1) {
          Swal.fire({
            icon: "warning",
            title: "Eligibility review not completed",
            text: "Please complete the dual citizenship check and agree to the terms and conditions first.",
            confirmButtonText: "OK"
          }).then(() => {
            this.$router.push("/Profile");
          });
          return;
        }

        // 2. 載入財務來源選項
        await this.loadFinancialResources();

        // 3. 載入訊息來源選項
        await this.loadMessageSources();

        // 4. 載入國家列表
        await this.loadCountries();

        // 5. 檢查並載入現有申請資料 (必須在選項載入後執行)
        await this.loadExistingApplication();
        
        // 1. 載入步驟內容
        await this.loadStepContent();

        // 6. 載入可申請的獎學金清單（在載入申請資料後，以便獲取 country）
        await this.loadAvailableScholarships(this.formData.country);

        // 7. 載入訂單資訊（在取得 asId 後）
        if (this.formData.asId && this.formData.asId !== -1) {
          await this.loadUserOrder();
        }

        // 8. 儲存初始狀態（用於後續比對變動）
        this.saveInitialState();
      } catch (error) {
        console.error("Form initialization error:", error);
        Swal.fire({
          icon: "error",
          title: "Initialization Failed",
          text: "Failed to initialize form. Please refresh the page.",
          confirmButtonText: "OK"
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 載入步驟內容
     */
    async loadStepContent() {
      const result = await applyStudentService.getActiveApplyStepContent(1, this.aId);
      if (result.success && result.data) {
        this.stepContent = result.data;
        if (!this.stepContent.title) {
          this.stepContent.title = 'Step 1';
        }
      } else {
        // 使用預設值
        this.stepContent = {
          step: 1,
          title: 'Step 1',
          content: '',
          applyInfo: null
        };
      }
    },

    /**
     * 載入財務來源選項
     */
    async loadFinancialResources() {
      const result = await applyStudentService.getAvailableFinances();
      if (result.success && result.data) {
        this.availableFinances = result.data;
        // 初始化選擇狀態
        this.availableFinances.forEach(item => {
          this.$set(this.selectedFinances, item.fId, {
            selected: false,
            otherText: '',
            scholarshipText: ''
          });
        });
      }
    },

    /**
     * 載入訊息來源選項
     */
    async loadMessageSources() {
      const result = await applyStudentService.getAvailableMessageSources();
      if (result.success && result.data) {
        this.availableMessageSources = result.data;
        // 初始化選擇狀態
        this.availableMessageSources.forEach(item => {
          this.$set(this.selectedMessageSources, item.msId, {
            selected: false,
            otherText: ''
          });
        });
      }
    },

    /**
     * 載入國家列表
     */
    async loadCountries() {
      const result = await applyStudentService.getAvailableCountries();
      if (result.success && result.data) {
        this.availableCountries = result.data;
      }
    },

    /**
     * 載入可申請的獎學金清單
     * @param {string} permCountry 學生的國籍代碼
     */
    async loadAvailableScholarships(permCountry = '') {
      const result = await applyStudentService.getAvailableScholarshipsByCountry(permCountry);
      if (result.success && result.data) {
        this.availableScholarships = result.data;

        // 初始化獎學金選擇狀態 - 使用 Vue.set 確保響應式
        const scholarshipSelectionsTemp = {};
        result.data.forEach(scholarship => {
          console.log("applyStudentScholarship:", scholarship.applyStudentScholarship);
          scholarshipSelectionsTemp[scholarship.sId] = {
            isApply: scholarship.applyStudentScholarship?.isApply || false,
            fileUrl: scholarship.applyStudentScholarship?.url || '',
            fileName: ''
          };
        });
        this.scholarshipSelections = scholarshipSelectionsTemp;
      } else {
        this.availableScholarships = [];
        this.scholarshipSelections = {};
      }
    },

    /**
     * 載入現有申請資料
     */
    async loadExistingApplication() {
      const result = await applyStudentService.getUserApplication();
      if (result.success && result.data) {
        this.aId = result.data.aId || null;
        this.populateFormData(result.data);
      }
    },

    /**
     * 填充表單資料
     */
    populateFormData(data) {
      this.formData.asId = data.asId || -1;

      // 檢查是否已提交
      this.isSubmitted = data.isSubmitted || false;

      // Personal Data
      this.formData.nameFirst = data.nameFirst || "";
      this.formData.nameMiddle = data.nameMiddle || "";
      this.formData.nameLast = data.nameLast || "";
      this.formData.chineseName = data.chineseName || "";
      this.formData.birth = data.dateOfBirth || "";
      this.formData.contactEmail = data.contactEmail || "";
      this.formData.gender = data.gender || 0;
      this.formData.maritalStatus = data.maritalStatus || 0;
      this.formData.nativeLanguage = data.nativeLanguage || "";

      // 大頭照
      this.formData.photo = data.photo || "";
      if (data.photoPreviewUrl) {
        this.previewUrl = data.photoPreviewUrl;
      }

      // Permanent Address
      this.formData.street = data.permStreet || "";
      this.formData.city = data.permCity || "";
      this.formData.country = data.permCountry || "";
      this.formData.zipCode = data.permZipCode || "";
      this.formData.phoneCountry = data.permPhoneCountry || "";
      this.formData.phoneArea = data.permPhoneArea || "";
      this.formData.phoneNumber = data.permPhoneNumber || "";

      // Mailing Address
      this.formData.mailingStreet = data.mailStreet || "";
      this.formData.mailingCity = data.mailCity || "";
      this.formData.mailingCountry = data.mailCountry || "";
      this.formData.mailingZipCode = data.mailZipCode || "";
      this.formData.mailingPhoneCountry = data.mailPhoneCountry || "";
      this.formData.mailingPhoneArea = data.mailPhoneArea || "";
      this.formData.mailingPhoneNumber = data.mailPhoneNumber || "";
      this.sameAsPermanent = data.sameAsPermanent || false;

      // Citizenship & Passport
      this.formData.countryOfBirth = data.countryOfBirth || "";
      this.formData.countryOfCitizenship = data.countryOfCitizenship || "";
      this.formData.passportNumber = data.passportNumber || "";

      // Financial Resources - Parse JSON
      if (data.financial) {
        try {
          const financialData = JSON.parse(data.financial);
          financialData.forEach(item => {
            if (this.selectedFinances[item.fId]) {
              this.selectedFinances[item.fId].selected = true;
              this.selectedFinances[item.fId].otherText = item.otherText || '';
              this.selectedFinances[item.fId].scholarshipText = item.scholarshipText || '';
            }
          });
        } catch (e) {
          console.error("Error parsing financial data:", e);
        }
      }

      // Scholarship Application
      this.formData.scholarshipApplication = data.scholarshipApplication || 0;

      // ITRI Program
      this.formData.isITRIProgram = data.isITRIProgram || 0;
      this.formData.iitName = data.iitName || "";
      this.formData.iitStudyField = data.iitStudyField || "";
      this.formData.iitGraduationDate = data.iitGraduationDate || "";
      this.formData.iitDegreeReceived = data.iitDegreeReceived || 0;
      this.formData.iitDegreeOther = data.iitDegreeOther || "";

      // Research Interest
      this.formData.interestSystemsSoftware = data.interestSystemsSoftware || false;
      this.formData.interest5GCommunications = data.interest5GCommunications || false;
      this.formData.interestMachineLearning = data.interestMachineLearning || false;
      this.formData.interestVRAR = data.interestVRAR || false;
      this.formData.interestDeepNeuralNetwork = data.interestDeepNeuralNetwork || false;
      this.formData.interestDrone = data.interestDrone || false;
      this.formData.interestCyberSecurity = data.interestCyberSecurity || false;
      this.formData.interestRobotics = data.interestRobotics || false;
      this.formData.interestAutonomousVehicles = data.interestAutonomousVehicles || false;
      this.formData.interest3DIC = data.interest3DIC || false;
      this.formData.interest3DPrinting = data.interest3DPrinting || false;

      // Prospective Advisor
      this.formData.professorName = data.professorName || "";
      this.formData.professorDepartment = data.professorDepartment || "";
      this.formData.professorEmail = data.professorEmail || "";
      this.formData.professorAgreeITRI = data.professorAgreeITRI || 0;

      // Declaration
      this.formData.declareIITAlumni = data.declareIITAlumni || false;
      this.formData.declareWillGraduate = data.declareWillGraduate || false;
      this.formData.declareInfoAccurate = data.declareInfoAccurate || false;

      // Father
      this.formData.fatherName = data.fatherName || "";
      this.formData.fatherCountryOfBirth = data.fatherCountryOfBirth || "";
      this.formData.fatherCountryOfCitizenship = data.fatherCountryOfCitizenship || "";
      this.formData.fatherPhoneCountry = data.fatherPhoneCountry || "";
      this.formData.fatherPhoneArea = data.fatherPhoneArea || "";
      this.formData.fatherPhoneNumber = data.fatherPhoneNumber || "";

      // Mother
      this.formData.motherName = data.motherName || "";
      this.formData.motherCountryOfBirth = data.motherCountryOfBirth || "";
      this.formData.motherCountryOfCitizenship = data.motherCountryOfCitizenship || "";
      this.formData.motherPhoneCountry = data.motherPhoneCountry || "";
      this.formData.motherPhoneArea = data.motherPhoneArea || "";
      this.formData.motherPhoneNumber = data.motherPhoneNumber || "";

      // Emergency Contact
      this.formData.emergencyName = data.emergencyName || "";
      this.formData.emergencyRelationship = data.emergencyRelationship || "";
      this.formData.emergencyEmail = data.emergencyEmail || "";
      this.formData.emergencyPhoneCountry = data.emergencyPhoneCountry || "";
      this.formData.emergencyPhoneArea = data.emergencyPhoneArea || "";
      this.formData.emergencyPhoneNumber = data.emergencyPhoneNumber || "";
      this.formData.emergencyStreet = data.emergencyStreet || "";
      this.formData.emergencyCity = data.emergencyCity || "";
      this.formData.emergencyCountry = data.emergencyCountry || "";

      // High School
      this.formData.highSchoolName = data.highSchoolName || "";
      this.formData.highSchoolStreet = data.highSchoolStreet || "";
      this.formData.highSchoolCity = data.highSchoolCity || "";
      this.formData.highSchoolCountry = data.highSchoolCountry || "";
      this.formData.highSchoolAttended = data.highSchoolAttended || "";
      this.formData.highSchoolGraduation = data.highSchoolGraduation || "";
      this.formData.highSchoolReceive = data.highSchoolReceive || "";

      // Undergraduate
      this.formData.undergraduateName = data.undergraduateName || "";
      this.formData.undergraduateStreet = data.undergraduateStreet || "";
      this.formData.undergraduateCity = data.undergraduateCity || "";
      this.formData.undergraduateCountry = data.undergraduateCountry || "";
      this.formData.undergraduateAttended = data.undergraduateAttended || "";
      this.formData.undergraduateGraduation = data.undergraduateGraduation || "";
      this.formData.undergraduateReceive = data.undergraduateReceive || "";

      // Graduate
      this.formData.graduateName = data.graduateName || "";
      this.formData.graduateStreet = data.graduateStreet || "";
      this.formData.graduateCity = data.graduateCity || "";
      this.formData.graduateCountry = data.graduateCountry || "";
      this.formData.graduateAttended = data.graduateAttended || "";
      this.formData.graduateGraduation = data.graduateGraduation || "";
      this.formData.graduateReceive = data.graduateReceive || "";

      // Studied in Taiwan
      this.formData.studiedInTaiwan = data.studiedInTaiwan || 0;
      this.formData.studiedInTaiwanDetail = data.studiedInTaiwanDetail || "";

      // Language Proficiency
      this.formData.languageEnglish = data.languageEnglish || 0;
      this.formData.languageMandarin = data.languageMandarin || 0;
      this.formData.languageOther = data.languageOther || false;
      this.formData.languageOtherName = data.languageOtherName || "";
      this.formData.languageOtherLevel = data.languageOtherLevel || 0;

      // Other Information
      this.formData.hasTaiwanARC = data.hasTaiwanARC || 0;
      this.formData.taiwanARCNumber = data.taiwanARCNumber || "";
      this.formData.hasTaiwanAPRC = data.hasTaiwanAPRC || 0;
      this.formData.taiwanAPRCNumber = data.taiwanAPRCNumber || "";

      // Message Source - Parse JSON
      if (data.messageSource) {
        try {
          const messageSourceData = JSON.parse(data.messageSource);
          messageSourceData.forEach(item => {
            if (this.selectedMessageSources[item.msId]) {
              this.selectedMessageSources[item.msId].selected = true;
              this.selectedMessageSources[item.msId].otherText = item.otherText || '';
            }
          });
        } catch (e) {
          console.error("Error parsing message source data:", e);
        }
      }
    },

    /**
     * 載入使用者訂單資訊
     */
    async loadUserOrder() {
      if (!this.formData.asId || this.formData.asId === -1) {
        console.warn("Cannot load order: asId is not available");
        return;
      }

      try {
        const result = await applyStudentService.hasUserOrder(this.formData.asId);

        if (result.success && result.data) {
          this.hasOrder = result.data.hasOrder;
          this.isPaid = (result.data.status === 1); // status === 1 表示已付款

          // 如果已付款，顯示通知
          if (this.isPaid) {
            console.log("Payment completed. Some fields will be disabled.");
          }
        } else {
          console.warn("Failed to load order information:", result.message);
          this.hasOrder = false;
          this.isPaid = false;
        }
      } catch (error) {
        console.error("Error loading user order:", error);
        this.hasOrder = false;
        this.isPaid = false;
      }
    },

    /**
     * 儲存初始狀態（用於後續比對變動）
     */
    saveInitialState() {
      // 深拷貝當前的表單資料
      this.initialFormData = JSON.parse(JSON.stringify(this.formData));

      // 深拷貝當前的財務來源選擇
      this.initialSelectedFinances = JSON.parse(JSON.stringify(this.selectedFinances));

      // 深拷貝當前的訊息來源選擇
      this.initialSelectedMessageSources = JSON.parse(JSON.stringify(this.selectedMessageSources));

      // 深拷貝當前的獎學金選擇
      this.initialScholarshipSelections = JSON.parse(JSON.stringify(this.scholarshipSelections));

      // 重置未儲存變動標記
      this.hasUnsavedChanges = false;
    },

    /**
     * 檢查是否有變動
     */
    checkForChanges() {
      // 如果還沒有初始資料，不進行比對
      if (!this.initialFormData) {
        return;
      }

      // 比對表單資料
      const formDataChanged = JSON.stringify(this.formData) !==
        JSON.stringify(this.initialFormData);

      // 比對財務來源選擇
      const financesChanged = JSON.stringify(this.selectedFinances) !==
        JSON.stringify(this.initialSelectedFinances);

      // 比對訊息來源選擇
      const messageSourcesChanged = JSON.stringify(this.selectedMessageSources) !==
        JSON.stringify(this.initialSelectedMessageSources);

      // 比對獎學金選擇
      const scholarshipsChanged = JSON.stringify(this.scholarshipSelections) !==
        JSON.stringify(this.initialScholarshipSelections);

      this.hasUnsavedChanges = formDataChanged || financesChanged ||
        messageSourcesChanged || scholarshipsChanged;
    },

    /**
     * 設置瀏覽器關閉/重新整理警告
     */
    setupBeforeUnloadHandler() {
      this.handleBeforeUnload = (e) => {
        if (this.hasUnsavedChanges) {
          e.preventDefault();
          e.returnValue = '';
          return '';
        }
      };
      window.addEventListener('beforeunload', this.handleBeforeUnload);
    },

    /**
     * 處理日期欄位 - 將空字串轉換為 null
     * @param {string} dateValue
     * @returns {string|null}
     */
    processDateField(dateValue) {
      return dateValue && dateValue.trim() !== '' ? dateValue : null;
    },

    /**
     * 準備儲存資料 - 將表單資料轉換為 API 格式
     */
    prepareSubmitData() {
      // 準備財務來源 JSON
      const financialArray = [];
      Object.keys(this.selectedFinances).forEach(fId => {
        const finance = this.selectedFinances[fId];
        if (finance.selected) {
          const item = { fId: parseInt(fId) };
          if (finance.otherText) {
            item.otherText = finance.otherText;
          }
          if (finance.scholarshipText) {
            item.scholarshipText = finance.scholarshipText;
          }
          financialArray.push(item);
        }
      });

      // 準備訊息來源 JSON
      const messageSourceArray = [];
      Object.keys(this.selectedMessageSources).forEach(msId => {
        const source = this.selectedMessageSources[msId];
        if (source.selected) {
          const item = { msId: parseInt(msId) };
          if (source.otherText) {
            item.otherText = source.otherText;
          }
          messageSourceArray.push(item);
        }
      });

      // 取得選擇的學期梯次 aId
      const selectedApplyId = localStorage.getItem('selectedApplyId');
      const aId = selectedApplyId ? parseInt(selectedApplyId, 10) : null;

      return {
        // 學期梯次 ID
        aId: aId,
        // Personal Data
        nameFirst: this.formData.nameFirst,
        nameMiddle: this.formData.nameMiddle,
        nameLast: this.formData.nameLast,
        chineseName: this.formData.chineseName,
        dateOfBirth: this.formData.birth,
        contactEmail: this.formData.contactEmail,
        gender: this.formData.gender,
        maritalStatus: this.formData.maritalStatus,
        nativeLanguage: this.formData.nativeLanguage,
        photo: this.formData.photo, // 包含 base64 編碼的照片資料或空字串

        // Permanent Address
        permStreet: this.formData.street,
        permCity: this.formData.city,
        permCountry: this.formData.country,
        permZipCode: this.formData.zipCode,
        permPhoneCountry: this.formData.phoneCountry,
        permPhoneArea: this.formData.phoneArea,
        permPhoneNumber: this.formData.phoneNumber,

        // Mailing Address
        mailStreet: this.formData.mailingStreet,
        mailCity: this.formData.mailingCity,
        mailCountry: this.formData.mailingCountry,
        mailZipCode: this.formData.mailingZipCode,
        mailPhoneCountry: this.formData.mailingPhoneCountry,
        mailPhoneArea: this.formData.mailingPhoneArea,
        mailPhoneNumber: this.formData.mailingPhoneNumber,
        sameAsPermanent: this.sameAsPermanent,

        // Citizenship & Passport
        countryOfBirth: this.formData.countryOfBirth,
        countryOfCitizenship: this.formData.countryOfCitizenship,
        passportNumber: this.formData.passportNumber,

        // Financial Resources (JSON string)
        financial: JSON.stringify(financialArray),

        // Scholarship Application
        scholarshipApplication: this.formData.scholarshipApplication,

        // ITRI Program
        isITRIProgram: this.formData.isITRIProgram,
        iitName: this.formData.iitName,
        iitStudyField: this.formData.iitStudyField,
        iitGraduationDate: this.formData.iitGraduationDate,
        iitDegreeReceived: this.formData.iitDegreeReceived,
        iitDegreeOther: this.formData.iitDegreeOther,

        // Research Interest
        interestSystemsSoftware: this.formData.interestSystemsSoftware,
        interest5GCommunications: this.formData.interest5GCommunications,
        interestMachineLearning: this.formData.interestMachineLearning,
        interestVRAR: this.formData.interestVRAR,
        interestDeepNeuralNetwork: this.formData.interestDeepNeuralNetwork,
        interestDrone: this.formData.interestDrone,
        interestCyberSecurity: this.formData.interestCyberSecurity,
        interestRobotics: this.formData.interestRobotics,
        interestAutonomousVehicles: this.formData.interestAutonomousVehicles,
        interest3DIC: this.formData.interest3DIC,
        interest3DPrinting: this.formData.interest3DPrinting,

        // Prospective Advisor
        professorName: this.formData.professorName,
        professorDepartment: this.formData.professorDepartment,
        professorEmail: this.formData.professorEmail,
        professorAgreeITRI: this.formData.professorAgreeITRI,

        // Declaration
        declareIITAlumni: this.formData.declareIITAlumni,
        declareWillGraduate: this.formData.declareWillGraduate,
        declareInfoAccurate: this.formData.declareInfoAccurate,

        // Father
        fatherName: this.formData.fatherName,
        fatherCountryOfBirth: this.formData.fatherCountryOfBirth,
        fatherCountryOfCitizenship: this.formData.fatherCountryOfCitizenship,
        fatherPhoneCountry: this.formData.fatherPhoneCountry,
        fatherPhoneArea: this.formData.fatherPhoneArea,
        fatherPhoneNumber: this.formData.fatherPhoneNumber,

        // Mother
        motherName: this.formData.motherName,
        motherCountryOfBirth: this.formData.motherCountryOfBirth,
        motherCountryOfCitizenship: this.formData.motherCountryOfCitizenship,
        motherPhoneCountry: this.formData.motherPhoneCountry,
        motherPhoneArea: this.formData.motherPhoneArea,
        motherPhoneNumber: this.formData.motherPhoneNumber,

        // Emergency Contact
        emergencyName: this.formData.emergencyName,
        emergencyRelationship: this.formData.emergencyRelationship,
        emergencyEmail: this.formData.emergencyEmail,
        emergencyPhoneCountry: this.formData.emergencyPhoneCountry,
        emergencyPhoneArea: this.formData.emergencyPhoneArea,
        emergencyPhoneNumber: this.formData.emergencyPhoneNumber,
        emergencyStreet: this.formData.emergencyStreet,
        emergencyCity: this.formData.emergencyCity,
        emergencyCountry: this.formData.emergencyCountry,

        // High School
        highSchoolName: this.formData.highSchoolName,
        highSchoolStreet: this.formData.highSchoolStreet,
        highSchoolCity: this.formData.highSchoolCity,
        highSchoolCountry: this.formData.highSchoolCountry,
        highSchoolAttended: this.processDateField(this.formData.highSchoolAttended),
        highSchoolGraduation: this.processDateField(this.formData.highSchoolGraduation),
        highSchoolReceive: this.formData.highSchoolReceive,

        // Undergraduate
        undergraduateName: this.formData.undergraduateName,
        undergraduateStreet: this.formData.undergraduateStreet,
        undergraduateCity: this.formData.undergraduateCity,
        undergraduateCountry: this.formData.undergraduateCountry,
        undergraduateAttended: this.processDateField(this.formData.undergraduateAttended),
        undergraduateGraduation: this.processDateField(this.formData.undergraduateGraduation),
        undergraduateReceive: this.formData.undergraduateReceive,

        // Graduate
        graduateName: this.formData.graduateName,
        graduateStreet: this.formData.graduateStreet,
        graduateCity: this.formData.graduateCity,
        graduateCountry: this.formData.graduateCountry,
        graduateAttended: this.processDateField(this.formData.graduateAttended),
        graduateGraduation: this.processDateField(this.formData.graduateGraduation),
        graduateReceive: this.formData.graduateReceive,

        // Studied in Taiwan
        studiedInTaiwan: this.formData.studiedInTaiwan,
        studiedInTaiwanDetail: this.formData.studiedInTaiwanDetail,

        // Language Proficiency
        languageEnglish: this.formData.languageEnglish,
        languageMandarin: this.formData.languageMandarin,
        languageOther: this.formData.languageOther,
        languageOtherName: this.formData.languageOtherName,
        languageOtherLevel: this.formData.languageOtherLevel,

        // Other Information
        hasTaiwanARC: this.formData.hasTaiwanARC,
        taiwanARCNumber: this.formData.taiwanARCNumber,
        hasTaiwanAPRC: this.formData.hasTaiwanAPRC,
        taiwanAPRCNumber: this.formData.taiwanAPRCNumber,

        // Message Source (JSON string)
        messageSource: JSON.stringify(messageSourceArray),

        // Scholarship Selections (JSON string)
        scholarshipSelections: JSON.stringify(this.scholarshipSelections),

        //applyPrograms: this.formData.applyPrograms,
      };
    },

    /**
     * 儲存申請表單
     */
    async saveApplication() {
      // 檢查是否已提交
      if (this.isSubmitted) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Modify",
          text: "Your application has been submitted and cannot be modified.",
          confirmButtonText: "OK"
        });
        return;
      }

      if (this.isSaving) return;

      // 啟用驗證錯誤顯示
      this.showValidationErrors = true;

      // 驗證必填欄位
      const validationErrors = this.validateRequiredFields();
      if (validationErrors.length > 0) {
        const errorMessage = "Please fill in the following required fields:<br><br>" +
          validationErrors.join("<br>");

        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          html: errorMessage,
          confirmButtonText: "OK",
          allowEscapeKey: false,
          allowOutsideClick: false,
          didClose: () => {
            // 使用者關閉視窗時也滾動到第一個錯誤欄位
            this.scrollToFirstError(validationErrors[0]);
          }
        });

        return;
      }

      this.isSaving = true;
      try {
        const submitData = this.prepareSubmitData();
        const result = await applyStudentService.saveUserApplication(submitData);

        if (result.success) {
          // 儲存成功後更新初始狀態
          this.saveInitialState();

          Swal.fire({
            icon: "success",
            title: "Success!",
            text: result.data.message || "Application saved successfully!",
            confirmButtonText: "OK",
            didClose: () => {
              // 重新載入現有申請資料以更新表單狀態
              this.loadAvailableScholarships(this.formData.country);
            }
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Save Failed",
            text: result.message || "Failed to save application. Please try again.",
            confirmButtonText: "OK"
          });
        }
      } catch (error) {
        console.error("Save application error:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred while saving. Please try again.",
          confirmButtonText: "OK"
        });
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * 靜默儲存申請表單（不顯示成功訊息）
     * 用於「Save and Leave」場景
     * @returns {Promise<boolean>} 是否儲存成功
     */
    async saveApplicationSilently() {
      if (this.isSaving) return false;

      // 啟用驗證錯誤顯示
      this.showValidationErrors = true;

      // 驗證必填欄位
      const validationErrors = this.validateRequiredFields();
      if (validationErrors.length > 0) {
        const errorMessage = "Please fill in the following required fields:<br><br>" +
          validationErrors.join("<br>");

        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          html: errorMessage,
          confirmButtonText: "OK",
          allowEscapeKey: false,
          allowOutsideClick: false,
          didClose: () => {
            this.scrollToFirstError(validationErrors[0]);
          }
        });

        return false;
      }

      this.isSaving = true;
      try {
        const submitData = this.prepareSubmitData();
        const result = await applyStudentService.saveUserApplication(submitData);

        if (result.success) {
          // 儲存成功後更新初始狀態
          this.saveInitialState();
          return true;
        } else {
          Swal.fire({
            icon: "error",
            title: "Save Failed",
            text: result.message || "Failed to save application. Please try again.",
            confirmButtonText: "OK"
          });
          return false;
        }
      } catch (error) {
        console.error("Save application error:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred while saving. Please try again.",
          confirmButtonText: "OK"
        });
        return false;
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * 驗證必填欄位
     * @returns {Array} 錯誤訊息陣列
     */
    validateRequiredFields() {
      const errors = [];

      // 1. Personal Data (個人資料)
      if (!this.formData.nameFirst || !this.formData.nameFirst.trim()) {
        errors.push("• English Name (First Name) is required");
      }
      if (!this.formData.nameLast || !this.formData.nameLast.trim()) {
        errors.push("• English Name (Last Name) is required");
      }
      if (!this.formData.birth) {
        errors.push("• Date of Birth is required");
      }
      if (!this.formData.contactEmail || !this.formData.contactEmail.trim()) {
        errors.push("• Contact Email is required");
      } else if (!this.isValidEmail(this.formData.contactEmail)) {
        errors.push("• Contact Email format is invalid");
      }
      if (!this.formData.gender || this.formData.gender === 0) {
        errors.push("• Gender is required");
      }
      if (!this.formData.nativeLanguage || !this.formData.nativeLanguage.trim()) {
        errors.push("• Native Language is required");
      }

      // 2. Permanent Address (永久地址)
      if (!this.formData.street || !this.formData.street.trim()) {
        errors.push("• Permanent Address - Street is required");
      }
      if (!this.formData.city || !this.formData.city.trim()) {
        errors.push("• Permanent Address - City is required");
      }
      if (!this.formData.country || !this.formData.country.trim()) {
        errors.push("• Permanent Address - Country is required");
      }
      if (!this.formData.zipCode || !this.formData.zipCode.trim()) {
        errors.push("• Permanent Address - Zip code is required");
      }
      if (!this.formData.phoneCountry || !this.formData.phoneCountry.trim()) {
        errors.push("• Permanent Address - Phone Country Code is required");
      }
      if (!this.formData.phoneArea || !this.formData.phoneArea.trim()) {
        errors.push("• Permanent Address - Phone Area Code is required");
      }
      if (!this.formData.phoneNumber || !this.formData.phoneNumber.trim()) {
        errors.push("• Permanent Address - Phone Number is required");
      }

      // 3. Mailing Address (郵寄地址)
      if (!this.formData.mailingStreet || !this.formData.mailingStreet.trim()) {
        errors.push("• Mailing Address - Street is required");
      }
      if (!this.formData.mailingCity || !this.formData.mailingCity.trim()) {
        errors.push("• Mailing Address - City is required");
      }
      if (!this.formData.mailingCountry || !this.formData.mailingCountry.trim()) {
        errors.push("• Mailing Address - Country is required");
      }
      if (!this.formData.mailingZipCode || !this.formData.mailingZipCode.trim()) {
        errors.push("• Mailing Address - Zip code is required");
      }
      if (!this.formData.mailingPhoneCountry || !this.formData.mailingPhoneCountry.trim()) {
        errors.push("• Mailing Address - Phone Country Code is required");
      }
      if (!this.formData.mailingPhoneArea || !this.formData.mailingPhoneArea.trim()) {
        errors.push("• Mailing Address - Phone Area Code is required");
      }
      if (!this.formData.mailingPhoneNumber || !this.formData.mailingPhoneNumber.trim()) {
        errors.push("• Mailing Address - Phone Number is required");
      }

      // 4. Citizenship & Passport (國籍與護照)
      if (!this.formData.countryOfBirth || !this.formData.countryOfBirth.trim()) {
        errors.push("• Country of Birth is required");
      }
      if (!this.formData.countryOfCitizenship || !this.formData.countryOfCitizenship.trim()) {
        errors.push("• Country of Citizenship is required");
      }

      // 5. Financial Resources (財務資源)
      const hasSelectedFinance = Object.values(this.selectedFinances).some(f => f.selected);
      if (!hasSelectedFinance) {
        errors.push("• Financial Support - At least one option must be selected");
      } else {
        // 檢查選中的財務來源是否需要填寫額外資訊
        Object.keys(this.selectedFinances).forEach(fId => {
          const finance = this.selectedFinances[fId];
          if (finance.selected) {
            const financeItem = this.availableFinances.find(f => f.fId === parseInt(fId));
            if (financeItem) {
              if (financeItem.isScholarship && (!finance.scholarshipText || !finance.scholarshipText.trim())) {
                errors.push(`• ${financeItem.name_EN || financeItem.name} - Scholarship name is required`);
              }
              if (financeItem.isOther && (!finance.otherText || !finance.otherText.trim())) {
                errors.push(`• ${financeItem.name_EN || financeItem.name} - Please specify`);
              }
            }
          }
        });
      }

      // 6. ITRI Program (工研院合作計畫)
      // if (!this.formData.isITRIProgram || this.formData.isITRIProgram === 0) {
      //   errors.push("• ITRI Program participation is required");
      // }

      // 7. Family Data - Father (父親資料)
      if (!this.formData.fatherName || !this.formData.fatherName.trim()) {
        errors.push("• Father Name is required");
      }
      if (!this.formData.fatherCountryOfBirth || !this.formData.fatherCountryOfBirth.trim()) {
        errors.push("• Father Country of Birth is required");
      }
      if (!this.formData.fatherCountryOfCitizenship || !this.formData.fatherCountryOfCitizenship.trim()) {
        errors.push("• Father Country of Citizenship is required");
      }
      if (!this.formData.fatherPhoneCountry || !this.formData.fatherPhoneCountry.trim()) {
        errors.push("• Father Phone Country Code is required");
      }
      if (!this.formData.fatherPhoneArea || !this.formData.fatherPhoneArea.trim()) {
        errors.push("• Father Phone Area Code is required");
      }
      if (!this.formData.fatherPhoneNumber || !this.formData.fatherPhoneNumber.trim()) {
        errors.push("• Father Phone Number is required");
      }

      // 8. Family Data - Mother (母親資料)
      if (!this.formData.motherName || !this.formData.motherName.trim()) {
        errors.push("• Mother Name is required");
      }
      if (!this.formData.motherCountryOfBirth || !this.formData.motherCountryOfBirth.trim()) {
        errors.push("• Mother Country of Birth is required");
      }
      if (!this.formData.motherCountryOfCitizenship || !this.formData.motherCountryOfCitizenship.trim()) {
        errors.push("• Mother Country of Citizenship is required");
      }
      if (!this.formData.motherPhoneCountry || !this.formData.motherPhoneCountry.trim()) {
        errors.push("• Mother Phone Country Code is required");
      }
      if (!this.formData.motherPhoneArea || !this.formData.motherPhoneArea.trim()) {
        errors.push("• Mother Phone Area Code is required");
      }
      if (!this.formData.motherPhoneNumber || !this.formData.motherPhoneNumber.trim()) {
        errors.push("• Mother Phone Number is required");
      }

      // 9. Emergency Contact (緊急聯絡人)
      if (!this.formData.emergencyName || !this.formData.emergencyName.trim()) {
        errors.push("• Emergency Contact Name is required");
      }
      if (!this.formData.emergencyRelationship || !this.formData.emergencyRelationship.trim()) {
        errors.push("• Emergency Contact Relationship is required");
      }
      if (!this.formData.emergencyEmail || !this.formData.emergencyEmail.trim()) {
        errors.push("• Emergency Contact Email is required");
      } else if (!this.isValidEmail(this.formData.emergencyEmail)) {
        errors.push("• Emergency Contact Email format is invalid");
      }
      if (!this.formData.emergencyPhoneCountry || !this.formData.emergencyPhoneCountry.trim()) {
        errors.push("• Emergency Contact Phone Country Code is required");
      }
      if (!this.formData.emergencyPhoneArea || !this.formData.emergencyPhoneArea.trim()) {
        errors.push("• Emergency Contact Phone Area Code is required");
      }
      if (!this.formData.emergencyPhoneNumber || !this.formData.emergencyPhoneNumber.trim()) {
        errors.push("• Emergency Contact Phone Number is required");
      }
      if (!this.formData.emergencyStreet || !this.formData.emergencyStreet.trim()) {
        errors.push("• Emergency Contact Street is required");
      }
      if (!this.formData.emergencyCity || !this.formData.emergencyCity.trim()) {
        errors.push("• Emergency Contact City is required");
      }
      if (!this.formData.emergencyCountry || !this.formData.emergencyCountry.trim()) {
        errors.push("• Emergency Contact Country is required");
      }

      // 10. Studying in Taiwan (曾在台灣就讀)
      if (!this.formData.studiedInTaiwan || this.formData.studiedInTaiwan === 0) {
        errors.push("• Studied in Taiwan question is required");
      } else if (this.formData.studiedInTaiwan == 1) {
        // 如果選擇 Yes，則必須填寫詳細資訊
        if (!this.formData.studiedInTaiwanDetail || !this.formData.studiedInTaiwanDetail.trim()) {
          errors.push("• Studied in Taiwan - Please specify school name and type of program");
        }
      }

      // 11. Language Proficiency (語言能力)
      if (!this.formData.languageEnglish || this.formData.languageEnglish === 0) {
        errors.push("• English Language Proficiency is required");
      }
      if (!this.formData.languageMandarin || this.formData.languageMandarin === 0) {
        errors.push("• Mandarin Language Proficiency is required");
      }
      // 如果填寫了其他語言名稱或選擇了程度，則都必須填寫
      if ((this.formData.languageOtherName && this.formData.languageOtherName.trim()) || (this.formData.languageOtherLevel && this.formData.languageOtherLevel !== 0)) {
        if (!this.formData.languageOtherName || !this.formData.languageOtherName.trim()) {
          errors.push("• Other Language Name is required");
        }
        if (!this.formData.languageOtherLevel || this.formData.languageOtherLevel === 0) {
          errors.push("• Other Language Proficiency Level is required");
        }
      }

      // 12. Other Information (其他資訊)
      if (!this.formData.hasTaiwanARC || this.formData.hasTaiwanARC === 0) {
        errors.push("• Taiwan ARC question is required");
      } else if (this.formData.hasTaiwanARC == 1) {
        // 如果選擇 Yes，則必須填寫 ARC 號碼
        if (!this.formData.taiwanARCNumber || !this.formData.taiwanARCNumber.trim()) {
          errors.push("• Taiwan ARC Number is required");
        }
      }

      if (!this.formData.hasTaiwanAPRC || this.formData.hasTaiwanAPRC === 0) {
        errors.push("• Taiwan APRC question is required");
      } else if (this.formData.hasTaiwanAPRC == 1) {
        // 如果選擇 Yes，則必須填寫 APRC 號碼
        if (!this.formData.taiwanAPRCNumber || !this.formData.taiwanAPRCNumber.trim()) {
          errors.push("• Taiwan APRC Number is required");
        }
      }

      // 13. Message Source (訊息來源)
      const hasSelectedMessageSource = Object.values(this.selectedMessageSources).some(s => s.selected);
      if (!hasSelectedMessageSource) {
        errors.push("• Message Source - At least one option must be selected");
      } else {
        // 檢查選中的訊息來源是否需要填寫額外資訊
        Object.keys(this.selectedMessageSources).forEach(msId => {
          const source = this.selectedMessageSources[msId];
          if (source.selected) {
            const sourceItem = this.availableMessageSources.find(s => s.msId === parseInt(msId));
            if (sourceItem && sourceItem.isOther && (!source.otherText || !source.otherText.trim())) {
              errors.push(`• ${sourceItem.name_EN || sourceItem.name} - Please specify`);
            }
          }
        });
      }

      // 14. Scholarship Application Documents (獎學金申請文件)
      if (this.availableScholarships && this.availableScholarships.length > 0) {
        this.availableScholarships.forEach(scholarship => {
          const selection = this.scholarshipSelections[scholarship.sId];
          // 如果選擇申請且需要上傳文件，但沒有上傳
          if (selection && selection.isApply && scholarship.needApplyDoc && !selection.fileUrl) {
            errors.push(`• ${scholarship.name_EN} - Application document is required`);
          }
        });
      }

      return errors;
    },

    /**
     * 驗證 Email 格式
     * @param {string} email
     * @returns {boolean}
     */
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    /**
     * 捲動到第一個錯誤欄位
     * @param {string} errorMessage
     */
    scrollToFirstError(errorMessage) {
      // 根據錯誤訊息找到對應的欄位 ID 並捲動
      const fieldMapping = {
        "English Name (First Name)": "nameFirst",
        "English Name (Last Name)": "nameLast",
        "Date of Birth": "birth",
        "Emergency Contact Email": "emergencyEmail",
        "Contact Email": "contactEmail",
        "Gender": "step2",
        "Native Language": "nativeLanguage",
        "Permanent Address - Street": "street",
        "Permanent Address - City": "city",
        "Permanent Address - Country": "country",
        "Father Name": "fatherName",
        "Mother Name": "motherName",
        "Emergency Contact Name": "emergencyName",
      };

      // 找到匹配的欄位 ID
      for (const [key, fieldId] of Object.entries(fieldMapping)) {
        if (errorMessage.includes(key)) {
          const element = document.getElementById(fieldId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
            break;
          }
        }
      }
    },

    // 進度步驟 不同狀態增加不同class
    getStepClass(index) {
      if (index < this.currentStep) {
        return "done"; // 已完成
      } else if (index === this.currentStep) {
        return "filling"; // 目前步驟
      }
    },

    startTour() {
      // 建立 driver 實例，開始導覽
      this.driverObj = driver({
        showProgress: true,
        progressText: "{{current}}/{{total}}",
        showButtons: true,
        nextBtnText: "Next",
        prevBtnText: "Back",
        doneBtnText: "End Tour",
        animate: false,
        steps: this.arr,
      });

      this.driverObj.drive();
    },

    gotoNext() {
      this.$router.push("/Apply/2").then(() => {
        //window.scrollTo(0, 0); // 滾到最上面
      }).catch(err => {
        // 捕獲導航錯誤（例如被 beforeRouteLeave 阻止）
        // 如果是 NavigationDuplicated 或其他非錯誤情況，不做處理
        if (err.name !== 'NavigationDuplicated') {
          console.log('Navigation was prevented:', err.message);
        }
      });
    },

    /**
     * 處理檔案選擇 - 大頭照上傳
     * @param {Event} event 檔案選擇事件
     */
    async onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 檢查檔案類型
      if (!file.type.startsWith('image/')) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Please select an image file (JPG, PNG, etc.)',
          confirmButtonText: 'OK'
        });
        return;
      }

      // 檢查檔案大小 (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'The file size exceeds 10MB. Please select a smaller file.',
          confirmButtonText: 'OK'
        });
        return;
      }

      // 讀取檔案並轉換為 Base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result; // 已包含 data:image/...;base64, 前綴

        // 先顯示預覽
        this.previewUrl = base64Data;

        // 上傳到伺服器
        await this.uploadPhotoToServer(base64Data);
      };

      reader.onerror = () => {
        Swal.fire({
          icon: 'error',
          title: 'File Read Error',
          text: 'Failed to read the file. Please try again.',
          confirmButtonText: 'OK'
        });
      };

      reader.readAsDataURL(file);
    },

    /**
     * 處理照片上傳 - 暫存 base64 資料,等待 Save 時一併傳送
     * @param {string} base64Data Base64 編碼的圖片資料
     */
    async uploadPhotoToServer(base64Data) {
      // 將 base64 資料暫存到 formData.photo
      this.formData.photo = base64Data;

      // 顯示成功訊息
      // Swal.fire({
      //   icon: 'success',
      //   title: 'Photo Ready',
      //   text: 'Photo will be uploaded when you save the form.',
      //   confirmButtonText: 'OK',
      //   timer: 2000
      // });
    },

    /**
     * 清除大頭照
     */
    async clearImage() {
      const confirmResult = await Swal.fire({
        title: 'Delete Photo?',
        text: 'Are you sure you want to delete this photo?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      });

      if (confirmResult.isConfirmed) {
        // 清除預覽和資料
        this.previewUrl = "";
        this.formData.photo = "";

        // 清除檔案輸入框
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }

        // Swal.fire({
        //   icon: 'success',
        //   title: 'Cleared',
        //   text: 'Photo has been removed. Remember to save the form.',
        //   confirmButtonText: 'OK',
        //   timer: 1500
        // });
      }
    },

    /**
     * 處理獎學金文件上傳
     * @param {Event} event 檔案選擇事件
     * @param {number} scholarshipId 獎學金ID
     */
    async onScholarshipFileChange(event, scholarshipId) {
      const file = event.target.files[0];
      if (!file) return;

      // 檢查檔案類型
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];

      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Please select a valid file (PDF, DOC, DOCX, JPG, PNG)',
          confirmButtonText: 'OK'
        });
        // 清除檔案輸入框
        event.target.value = "";
        return;
      }

      // 檢查檔案大小 (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'The file size exceeds 10MB. Please select a smaller file.',
          confirmButtonText: 'OK'
        });
        // 清除檔案輸入框
        event.target.value = "";
        return;
      }

      // 讀取檔案並轉換為 Base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target.result; // 已包含 data:...;base64, 前綴

        // 儲存到 scholarshipSelections
        this.scholarshipSelections[scholarshipId].fileUrl = base64Data;
        this.scholarshipSelections[scholarshipId].fileName = file.name;

        Swal.fire({
          icon: 'success',
          title: 'File Uploaded',
          text: 'File uploaded successfully. Please remember to save the form.',
          confirmButtonText: 'OK'
        });
      };

      reader.onerror = () => {
        Swal.fire({
          icon: 'error',
          title: 'File Read Error',
          text: 'Failed to read the file. Please try again.',
          confirmButtonText: 'OK'
        });
        // 清除檔案輸入框
        event.target.value = "";
      };

      reader.readAsDataURL(file);
    },

    /**
     * 下載獎學金文件
     * @param {number} scholarshipId 獎學金ID
     */
    downloadScholarshipFile(scholarshipId) {
      const fileUrl = this.scholarshipSelections[scholarshipId].fileUrl;

      if (!fileUrl) {
        Swal.fire({
          icon: 'error',
          title: 'No File',
          text: 'No file available to download.',
          confirmButtonText: 'OK'
        });
        return;
      }

      // 如果是 base64 資料，直接下載
      if (fileUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = this.scholarshipSelections[scholarshipId].fileName || `scholarship_${scholarshipId}_document`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // 如果是伺服器路徑，在新視窗打開
        window.open(this.$store.state.src + this.$store.state.subDirectory + fileUrl, '_blank');
      }
    },

    /**
     * 移除獎學金文件
     * @param {number} scholarshipId 獎學金ID
     */
    async removeScholarshipFile(scholarshipId) {
      const confirmResult = await Swal.fire({
        title: 'Remove Document?',
        text: 'Are you sure you want to remove this document?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove it',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      });

      if (confirmResult.isConfirmed) {
        // 清除檔案資料
        this.scholarshipSelections[scholarshipId].fileUrl = '';
        this.scholarshipSelections[scholarshipId].fileName = '';

        // 清除檔案輸入框
        const fileInput = document.getElementById(`scholarship_file_${scholarshipId}`);
        if (fileInput) {
          fileInput.value = "";
        }

        Swal.fire({
          icon: 'success',
          title: 'Removed',
          text: 'Document has been removed. Remember to save the form.',
          confirmButtonText: 'OK',
        });
      }
    },

    /**
     * 切換表單元素的禁用狀態
     * @param {boolean} disable 是否禁用
     */
    toggleFormElements(disable) {
      // 使用 setTimeout 確保 DOM 已完全渲染
      setTimeout(() => {
        const container = document.querySelector('.box');
        if (!container) return;

        // 禁用所有 input (包含 text, radio, checkbox, file 等)
        const inputs = container.querySelectorAll('input:not(.tours_btn)');
        inputs.forEach(input => {
          input.disabled = disable;
        });

        // 禁用所有 select
        const selects = container.querySelectorAll('select');
        selects.forEach(select => {
          select.disabled = disable;
        });

        // 禁用所有 textarea
        const textareas = container.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          textarea.disabled = disable;
        });

        // 禁用所有按鈕 (除了使用說明按鈕和 Header 區的按鈕)
        const buttons = container.querySelectorAll('button:not(.tours_btn):not(.head-btn)');
        buttons.forEach(button => {
          button.disabled = disable;
        });
      }, 100);
    },
  },
};
</script>

<style scoped>
/* 當表單已提交時，禁用所有輸入元素 */
.submitted-readonly input,
.submitted-readonly select,
.submitted-readonly textarea,
.submitted-readonly button:not(.tours_btn) {
  pointer-events: none !important;
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

.submitted-readonly .form-check-input,
.submitted-readonly input[type="radio"],
.submitted-readonly input[type="checkbox"] {
  pointer-events: none !important;
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

.submitted-readonly .form-check-label {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}
</style>
